import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'fi' | 'en' | 'sv';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations: Record<Language, Record<string, string>> = {
  fi: {
    // Navigation
    'nav.home': 'Etusivu',
    'nav.services': 'Palvelut',
    'nav.pricing': 'Hinnasto',
    'nav.faq': 'UKK',
    'nav.shipping': 'Toimitus',
    'nav.contact': 'Yhteystiedot',
    'nav.callNow': 'Soita nyt',

    // Homepage Hero
    'home.hero.badge': 'Potkurikorjausta ja varaosia',
    'home.hero.title': 'Luotettavaa Potkurikorjausta',
    'home.hero.titleHighlight': 'Pirkanmaalla',
    'home.hero.subtitle': 'Korjaamme potkureita ammattitaidolla. Ota yhteyttä ja kerro tarpeesi!',
    'home.hero.cta.message': 'Lähetä viesti',
    'home.hero.cta.call': 'Soita +358 400 416 466',

    // Homepage Features
    'home.features.quality.title': 'Laatua ennen nopeutta',
    'home.features.quality.desc': 'Huolellista työtä',
    'home.features.warranty.title': '12kk takuu',
    'home.features.warranty.desc': 'Kaikille korjauksille',
    'home.features.satisfaction.title': 'Tavoitteena tyytyväisyys',
    'home.features.satisfaction.desc': 'Toimivuutta tavoitellen',
    'home.features.experience.title': '100+ korjausta',
    'home.features.experience.desc': 'Monipuolista kokemusta',

    // Homepage Services Preview
    'home.services.title': 'Palvelumme',
    'home.services.subtitle': 'Kattava valikoima potkurikorjaus- ja huoltopalveluita',
    'home.services.repair.title': 'Potkurikorjaukset',
    'home.services.repair.desc': 'Taipuneiden lapojen oikaisu, halkeamien hitsaus, vaurioiden korjaus',
    'home.services.repair.price': 'Alkaen 95€',
    'home.services.maintenance.title': 'Huolto & Kunnostus',
    'home.services.maintenance.desc': 'Tasapainotus, kiillotus, pinnoitus, antifouling-käsittely',
    'home.services.maintenance.price': 'Alkaen 80€',
    'home.services.parts.title': 'Varaosat',
    'home.services.parts.desc': 'Potkurit, moottorin osat, huolto-osat ja muut osat',
    'home.services.parts.price': 'Kysy tarjous',
    'home.services.viewAll': 'Katso kaikki palvelut',
    'home.services.inspection.title': 'Tarkastus',
    'home.services.inspection.desc': 'Perusteellinen kunnon arviointi ja dokumentointi',

    // Homepage Process
    'home.process.title': 'Näin korjaus etenee',
    'home.process.subtitle': '7 selkeää vaihetta',
    'home.process.step1.title': '1. Yhteydenotto',
    'home.process.step1.desc': 'Ota yhteyttä puhelimitse, sähköpostilla tai käytä laskuria hinta-arvioon.',
    'home.process.step2.title': '2. Potkurin toimitus',
    'home.process.step2.desc': 'Tuo potkuri toimipisteellemme, lähetä postitse tai tilaa noutopalvelu.',
    'home.process.step3.title': '3. Tarkastus',
    'home.process.step3.desc': 'Tarkastamme potkurin perusteellisesti ja dokumentoimme vauriot.',
    'home.process.step4.title': '4. Tarjous',
    'home.process.step4.desc': 'Olemme yhteydessä ja ilmoitamme hinnan.',
    'home.process.step5.title': '5. Korjaus',
    'home.process.step5.desc': 'Hyväksytyn tarjouksen jälkeen aloitamme korjauksen ammattitaidolla.',
    'home.process.step6.title': '6. Laaduntarkastus',
    'home.process.step6.desc': 'Tarkastamme työn laadun, tasapainotamme ja testaamme.',
    'home.process.step7.title': '7. Toimitus',
    'home.process.step7.desc': 'Ilmoitamme valmistumisesta. Nouto tai toimitamme perille.',

    // Homepage CTA
    'home.cta.title': 'Tarvitsetko potkurikorjausta?',
    'home.cta.subtitle': 'Laske hinta ilmaisella laskurilla tai ota yhteyttä suoraan.',
    'home.cta.button': 'Ota yhteyttä',

    // Homepage Why Choose Us
    'home.why.title': 'Miksi valita meidät?',
    'home.why.warranty.title': 'Takuu',
    'home.why.warranty.desc': '12 kuukauden takuu kaikille töille',
    'home.why.expertise.title': 'Ammattitaito',
    'home.why.expertise.desc': 'Kokemusta monipuolisista korjauksista',
    'home.why.quality.title': 'Laatu',
    'home.why.quality.desc': 'Käytämme hyviä materiaaleja ja menetelmiä',
    'home.why.flexibility.title': 'Joustavuus',
    'home.why.flexibility.desc': 'Myös kiiretyöt ja pikapalvelu',
    'home.why.service.title': 'Asiakaspalvelu',
    'home.why.service.desc': 'Henkilökohtainen palvelu suomeksi ja englanniksi',
    'home.why.pricing.title': 'Läpinäkyvä hinnoittelu',
    'home.why.pricing.desc': 'Selkeät hinnat ilman yllätyksiä',

    // Services Page
    'services.title': 'Palvelumme',
    'services.subtitle': 'Kattava valikoima potkurikorjaus- ja huoltopalveluita. Ammattitaitoa ja laatua.',
    'services.repair.title': 'Potkurikorjaukset',
    'services.repair.desc': 'Ammattitaitoista korjauspalvelua kaikille potkurityypeille',
    'services.repair.feature1': 'Taipuneiden lapojen oikaisu',
    'services.repair.feature2': 'Halkeamien hitsaus',
    'services.repair.feature3': 'Lohkeamien korjaus',
    'services.repair.feature4': 'Särökorjaukset',
    'services.repair.feature5': 'Kavitaatiovaurioiden korjaus',
    'services.maintenance.title': 'Huolto & Kunnostus',
    'services.maintenance.desc': 'Säännöllinen huolto pidentää potkurin käyttöikää',
    'services.maintenance.feature1': 'Tasapainotus',
    'services.maintenance.feature2': 'Kiillotus',
    'services.maintenance.feature3': 'Pinnoitus',
    'services.maintenance.feature4': 'Antifouling-käsittely',
    'services.maintenance.feature5': 'Holkkien vaihto',
    'services.parts.title': 'Varaosat',
    'services.parts.desc': 'Potkurit, moottorin osat, huolto-osat ja muut osat',
    'services.parts.feature1': 'Potkurit kaikille moottoreille',
    'services.parts.feature2': 'Moottorin osat',
    'services.parts.feature3': 'Huolto-osat',
    'services.parts.feature4': 'Asennus saatavilla',
    'services.parts.feature5': 'Neuvonta ja suositukset',
    'services.inspection.title': 'Tarkastus ja Vauriolausumat',
    'services.inspection.desc': 'Perusteellinen kunnon arviointi ja dokumentointi',
    'services.inspection.feature1': 'Visuaalinen tarkastus',
    'services.inspection.feature2': 'Mittaukset',
    'services.inspection.feature3': 'Vauriokartoitus',
    'services.inspection.feature4': 'Kirjallinen raportti',
    'services.inspection.feature5': 'Korjaussuositus',
    'services.emergency.title': 'Pikapalvelu',
    'services.emergency.desc': 'Kiireelliset korjaukset nopealla toimitusajalla',
    'services.emergency.feature1': 'Kiirekorjaukset',
    'services.emergency.feature2': 'Väliaikaisratkaisut',
    'services.emergency.feature3': 'Nouto ja toimitus',
    'services.emergency.feature4': 'Varapotkurit',
    'services.emergency.feature5': 'Priorisoimme työn',
    'services.whyChoose': 'Miksi valita meidät?',
    'services.from': 'Alkaen',
    'services.askQuote': 'Kysy tarjous',
    'services.repair.price': 'Alkaen 95€',
    'services.maintenance.price': 'Alkaen 50€',
    'services.inspection.price': 'Alkaen 30€',
    'services.emergency.price': '0-24h: +100%',

    // Pricing Page
    'pricing.title': 'Hinnasto',
    'pricing.subtitle': 'Valitse alta vaihtoehdot ja laske arvioitu hinta',
    'pricing.material': 'Materiaali',
    'pricing.material.note': 'Joissakin tapauksissa uusi potkuri voi olla edullisempi vaihtoehto.',
    'pricing.material.titanium': 'Titaani',
    'pricing.material.titanium.desc': 'Premium-materiaali erikoiskäyttöön',
    'pricing.material.bronze': 'Pronssi',
    'pricing.material.bronze.desc': 'Perinteinen merikäyttöön',
    'pricing.material.stainless': 'Ruostumaton teräs',
    'pricing.material.stainless.desc': 'Kestävä ja vahva materiaali',
    'pricing.material.aluminum': 'Alumiini',
    'pricing.material.aluminum.desc': 'Kevyt ja yleinen materiaali',
    'pricing.size': 'Potkurin koko',
    'pricing.size.basic': 'Perus koko',
    'pricing.size.medium': 'Keskikokoiset',
    'pricing.size.large': 'Suuret potkurit',
    'pricing.size.xlarge': 'Erittäin suuret',
    'pricing.size.professional': 'Ammattikäyttö',
    'pricing.duoprop': 'Duoprop (kaksoispotkuri)',
    'pricing.services': 'Lisäpalvelut',
    'pricing.services.duopropNote': 'Duoprop valittu: merkityt palvelut x2',
    'pricing.services.balancing': 'Tasapainotus',
    'pricing.services.balancing.desc': 'Vähentää tärinää',
    'pricing.services.polishing': 'Kiillotus',
    'pricing.services.polishing.desc': 'Parantaa hyötysuhdetta',
    'pricing.services.coating': 'Pinnoitus',
    'pricing.services.coating.desc': 'Suojaa korroosiolta',
    'pricing.services.bushing': 'Holkin vaihto',
    'pricing.services.bushing.desc': 'Vaihtotyö',
    'pricing.services.documentation': 'Dokumentointi',
    'pricing.services.documentation.desc': 'Korjausraportti',
    'pricing.services.inspection': 'Tarkastusmaksu',
    'pricing.services.inspection.desc': 'Jos korjausta ei tehdä',
    'pricing.services.shipping': 'Toimitus',
    'pricing.services.shipping.desc': 'Palautusrahti asiakkaalle',
    'pricing.urgency': 'Aikataulu',
    'pricing.urgency.normal': 'Normaali',
    'pricing.urgency.normal.time': 'Työjonon mukaisesti',
    'pricing.urgency.urgent': 'Kiireellinen',
    'pricing.urgency.urgent.time': '2-4 päivää',
    'pricing.urgency.veryUrgent': 'Erittäin kiireellinen',
    'pricing.urgency.veryUrgent.time': '1-2 päivää',
    'pricing.urgency.emergency': 'Hätätyö',
    'pricing.urgency.emergency.time': 'Sama päivä',
    'pricing.estimate': 'Hinta-arvio',
    'pricing.estimate.total': 'Arvioitu kokonaishinta',
    'pricing.estimate.repair': 'Korjaustyö',
    'pricing.estimate.services': 'Lisäpalvelut',
    'pricing.estimate.vat': 'ALV 25.5% sisältyy hintaan. Lopullinen hinta vahvistetaan tarkastuksen jälkeen.',
    'pricing.estimate.inspectionVat': 'Veloitetaan jos korjausta ei tehdä. ALV 25.5% sisältyy hintaan.',
    'pricing.estimate.select': 'Valitse materiaali ja koko nähdäksesi hinta-arvion',
    'pricing.sendQuote': 'Lähetä tarjouspyyntö',
    'pricing.help.title': 'Tarvitsetko apua hinnoittelussa?',
    'pricing.help.subtitle': 'Ota yhteyttä niin autamme sinua',
    'pricing.help.call': 'Soita',
    'pricing.common': 'Yleinen',
    'pricing.starting': 'Alk.',
    'pricing.blades': 'Lapojen määrä',
    'pricing.blades.label': 'lapaa',
    'pricing.blades.2': '2',
    'pricing.blades.3': '3',
    'pricing.blades.4': '4',
    'pricing.blades.5': '5',
    'pricing.blades.6plus': '6+',

    // Contact Page
    'contact.title': 'Ota Yhteyttä',
    'contact.subtitle': 'Autamme mielellämme kaikissa potkurikorjauksiin liittyvissä kysymyksissä.',
    'contact.info': 'Yhteystiedot',
    'contact.phone': 'Puhelin',
    'contact.phone.hours': 'Ma-Pe 8-16',
    'contact.email': 'Sähköposti',
    'contact.email.response': 'Vastaamme 24h sisällä',
    'contact.location': 'Sijainti',
    'contact.location.area': 'Toimialue: Akaa / Sastamala / Urjala',
    'contact.hours': 'Aukioloajat',
    'contact.hours.weekday': 'Maanantai - Perjantai: 8:00 - 16:00',
    'contact.hours.weekend': 'Viikonloppu: Sopimuksen mukaan',
    'contact.questions': 'Kysyttävää?',
    'contact.questions.desc': 'Soita niin selvitetään yhdessä miten voimme auttaa.',
    'contact.form.title': 'Lähetä viesti',
    'contact.form.estimate': 'Hinta-arvio hinnastosta',
    'contact.form.prefilled': 'Tiedot on esitäytetty. Täytä yhteystietosi ja lähetä tarjouspyyntö.',
    'contact.form.name': 'Nimi',
    'contact.form.email': 'Sähköposti',
    'contact.form.phone': 'Puhelin',
    'contact.form.propellerType': 'Potkurityyppi',
    'contact.form.propellerType.select': 'Valitse...',
    'contact.form.propellerType.aluminum': 'Alumiini',
    'contact.form.propellerType.stainless': 'Ruostumaton teräs',
    'contact.form.propellerType.bronze': 'Pronssi',
    'contact.form.propellerType.other': 'Muu potkurityyppi',
    'contact.form.duoprop': 'Duoprop',
    'contact.form.priority': 'Kiireellisyys / Prioriteetti',
    'contact.form.priority.normal': 'Normaali',
    'contact.form.priority.normal.desc': 'Ei kiirettä',
    'contact.form.priority.moderate': 'Kohtuullinen',
    'contact.form.priority.moderate.desc': '1-2 viikkoa',
    'contact.form.priority.urgent': 'Kiireellinen',
    'contact.form.priority.urgent.desc': 'Alle viikko',
    'contact.form.priority.emergency': 'Hätätyö',
    'contact.form.priority.emergency.desc': 'Heti',
    'contact.form.delivery': 'Toimitus',
    'contact.form.delivery.bring': 'Tuon itse toimipisteelle',
    'contact.form.delivery.send': 'Lähetän postitse',
    'contact.form.delivery.pickup': 'Toivon noutopalvelua',
    'contact.form.message': 'Viesti',
    'contact.form.message.placeholder': 'Kerro potkurin vauriosta ja mitä korjausta tarvitset...',
    'contact.form.images': 'Kuvat (valinnainen)',
    'contact.form.images.desc': 'Lisää kuvia vauriosta - max 5 kuvaa, 5 MB / kuva',
    'contact.form.images.add': 'Lisää kuvia',
    'contact.form.images.addMore': 'Lisää lisää kuvia',
    'contact.form.submit': 'Lähetä viesti',
    'contact.form.submitting': 'Lähetetään...',
    'contact.form.success': 'Viesti lähetetty! Vastaamme mahdollisimman pian.',
    'contact.form.error': 'Viestin lähetys epäonnistui. Yritä uudelleen tai ota yhteyttä puhelimitse.',
    'contact.form.validation.contact': 'Anna joko sähköposti tai puhelinnumero (tai molemmat)',
    'contact.form.validation.tooFast': 'Lomake täytettiin liian nopeasti. Odota hetki ja yritä uudelleen.',
    'contact.form.validation.rateLimit': 'Odota {seconds} sekuntia ennen seuraavaa lähetystä.',
    'contact.form.validation.maxImages': 'Voit lisätä enintään {max} kuvaa',
    'contact.form.validation.imageType': 'Vain kuvatiedostot sallittu',
    'contact.form.validation.imageSize': 'Kuvan maksimikoko on 5 MB',
    'contact.map': 'Sijainti',

    // FAQ Page
    'faq.title': 'Usein Kysytyt Kysymykset',
    'faq.subtitle': 'Vastauksia yleisimpiin kysymyksiin potkurikorjauksista',
    'faq.q1': 'Paljonko potkurin korjaus maksaa?',
    'faq.a1': 'Hinnat alkavat 95€ yksinkertaisista korjauksista. Koska kyseessä on käsityötä, palvelu ei välttämättä ole kaikille järkevä ratkaisu. Lopullinen hinta riippuu vaurion laajuudesta, potkurin koosta ja tarvittavista töistä. Käytä ilmaista laskuriamme saadaksesi välittömän hinta-arvion tai ota yhteyttä tarkkaa tarjousta varten.',
    'faq.q2': 'Kuinka kauan korjaus kestää?',
    'faq.a2': 'Normaalit korjaukset valmistuvat 3-7 päivässä. Huomioithan että työjonon pituus voi vaikuttaa toimitusaikaan. Monimutkaiset korjaukset saattavat vaatia hieman enemmän aikaa. Ilmoitamme tarkan toimitusajan tarjouksen yhteydessä.',
    'faq.q3': 'Minkä tyyppisiä potkureita korjaatte?',
    'faq.a3': 'Korjaamme pääsääntöisesti alumiinista, ruostumattomasta teräksestä ja pronssiseoksista valmistettuja venepotkureita. Myös vesijetti-impellereitä on käynyt jonkin verran huollossa.',
    'faq.q4': 'Tarjoatteko nouto- ja toimituspalvelua?',
    'faq.a4': 'Kyllä. Tarjoamme nouto- ja toimituspalvelua lisämaksusta. Voit myös lähettää potkurin meille postitse tai tuoda sen itse korjaamolle. Katso tarkemmat tiedot Toimitus-sivultamme.',
    'faq.q5': 'Onko korjauksille takuuta?',
    'faq.a5': 'Kyllä! Kaikille korjauksille 12 kuukauden takuu. Takuu kattaa työvirheet ja materiaaliviat. Takuu ei kata normaalista käytöstä johtuvia kulumisia tai uusia vaurioita.',
    'faq.q6': 'Voitteko korjata pahasti vaurioituneen potkurin?',
    'faq.a6': 'Useimmiten kyllä! Korjaamme myös pahasti vaurioituneita potkureita: taipuneet lavat, halkeamat, lohkeamat, kavitaatiovauriot. Arvioimme aina ensin onko korjaus taloudellisesti järkevää vs. uusi potkuri.',
    'faq.q7': 'Teettekö kiireellisiä korjauksia?',
    'faq.a7': 'Kyllä! Tarjoamme kiireellistä palvelua lisämaksusta. Soita suoraan +358 400 416 466 jos tarvitset kiireellistä korjausta. Katso lisätiedot Hinnasto-sivultamme.',
    'faq.q8': 'Miten maksun voi suorittaa?',
    'faq.a8': 'Hyväksymme käteisen, pankkisiirron ja laskun (yritysasiakkaat). Maksu suoritetaan ennen potkurin luovutusta. Lähetämme laskun sähköpostilla heti kun työ on valmis. Käteismaksu onnistuu noudettaessa, josta kuitti lähetetään sähköpostitse.',
    'faq.noAnswer': 'Eikö vastausta löytynyt?',
    'faq.noAnswerDesc': 'Ota yhteyttä, niin vastaamme kysymykseesi henkilökohtaisesti.',

    // Shipping Page
    'shipping.title': 'Toimitus & Logistiikka',
    'shipping.subtitle': 'Useita tapoja toimittaa potkuri meille ja takaisin.',
    'shipping.options': 'Toimitusvaihtoehdot',
    'shipping.bringYourself': 'Tuo itse',
    'shipping.bringYourselfDesc': 'Nopein ja helpoin tapa. Toimimme työtilassa, jossa ovet ovat lukittuina eikä paikalla olla jatkuvasti. Käynnit sovitaan etukäteen.',
    'shipping.free': 'Ilmainen',
    'shipping.byAppointment': 'Sovittavissa Ma-Pe 8-16',
    'shipping.postIt': 'Postita meille',
    'shipping.postItDesc': 'Pakkaa huolellisesti ja lähetä Matkahuolto/Posti. Ohjeet alla.',
    'shipping.ownCost': 'Oma kulu',
    'shipping.usually': 'Yleensä 10-25€',
    'shipping.mostPopular': 'Suosituin',
    'shipping.pickupService': 'Noutopalvelu',
    'shipping.pickupServiceDesc': 'Noudamme potkurin luotasi ja toimitamme takaisin valmiin.',
    'shipping.perKm': '1.21 €/km',
    'shipping.dependsOnDistance': 'Hinta riippuu etäisyydestä',
    'shipping.calculatePrice': 'Laske hinta',
    'shipping.priceCalculator': 'Noutopalvelun hintalaskuri',
    'shipping.fromAddress': 'Lähtöosoite',
    'shipping.addressPlaceholder': 'Syötä osoitteesi',
    'shipping.serviceType': 'Palvelun tyyppi',
    'shipping.pickupOnly': 'Vain nouto',
    'shipping.deliveryOnly': 'Vain palautus',
    'shipping.pickupAndDelivery': 'Nouto ja palautus',
    'shipping.oneWayDistance': 'Matka yhteen suuntaan',
    'shipping.pricePerKm': 'Kilometrihinta',
    'shipping.estimatedPrice': 'Arvioitu hinta',
    'shipping.priceNote': 'Huom: Tämä on arvio. Lopullinen hinta vahvistetaan tarjouksessa.',
    'shipping.packagingInstructions': 'Pakkausohjeet postitukseen',
    'shipping.packStep1': 'Kuivaa potkuri ennen pakkausta',
    'shipping.packStep2': 'Käytä tukevaa pahvilaatikkoa tai puulaatikkoa',
    'shipping.packStep3': 'Täytä tyhjät tilat sanomalehdellä, kuplamuovilla tai vastaavalla',
    'shipping.packStep4': 'Liitä pakettiin mukaan yhteystietosi ja lyhyt kuvaus vauriosta',
    'shipping.pickupArea': 'Noutopalvelu alue',
    'shipping.pickupAreaDesc': 'Tarjoamme noutopalvelua pääasiassa Pirkanmaan alueella.',
    'shipping.deliveryTime': 'Toimitusaika takaisin',
    'shipping.pickupTime': 'Nouto: Sovitaan erikseen',
    'shipping.postTime': 'Posti: 1-3 arkipäivää',
    'shipping.needHelp': 'Tarvitsetko apua toimituksessa?',
    'shipping.needHelpDesc': 'Soita niin autamme parhaalla tavalla.',

    // Footer
    'footer.services': 'Palvelut',
    'footer.contact': 'Yhteystiedot',
    'footer.hours': 'Aukioloajat',
    'footer.weekdays': 'Ma-Pe: 8-16',
    'footer.weekend': 'La-Su: Sopimuksesta',
    'footer.privacy': 'Tietosuoja',
    'footer.terms': 'Käyttöehdot',
    'footer.copyright': 'Kaikki oikeudet pidätetään.',
    'footer.tagline': 'Ammattitaitoista potkurikorjausta',
    'footer.description': 'Korjaamme venepotkurit ammattitaidolla. Alumiini-, teräs- ja pronssipotkurit. 12 kuukauden takuu kaikille töille.',
    'footer.businessId': 'Y-tunnus',
    'footer.founded': 'Perustettu',
    'footer.quickLinks': 'Pikalinkit',
    'footer.madeWith': 'Tehty',
    'footer.inFinland': 'Suomessa',

    // Common
    'common.learnMore': 'Lue lisää',
    'common.callNow': 'Soita nyt',
    'common.from': 'Alkaen',
    'common.yes': 'Kyllä',
    'common.no': 'Ei',
    'common.notProvided': 'Ei annettu',

    // Terms Page
    'terms.title': 'Käyttöehdot',
    'terms.subtitle': 'Potkurikorjaus verkkosivuston ja palveluiden käyttöehdot',
    'terms.section1.title': '1. Yleiset ehdot ja soveltamisala',
    'terms.section1.p1': 'Nämä yleiset sopimusehdot ("Ehdot") koskevat FennoFuture:n (Y-tunnus 3203489-7, "Yritys", "me", "meidän") tarjoamia tuotteita ja palveluita sekä verkkosivuston käyttöä. Käyttämällä palveluitamme tai tekemällä tilauksen hyväksyt nämä ehdot ja sitoudut noudattamaan niitä.',
    'terms.section1.p2': 'Nämä ehdot muodostavat oikeudellisesti sitovan sopimuksen asiakkaan ja yrityksen välille. Pidätämme oikeuden muuttaa näitä ehtoja ilmoittamalla muutoksista verkkosivustollamme vähintään 30 päivää ennen muutosten voimaantuloa. Olemassa oleviin sopimuksiin sovelletaan tilaushetkellä voimassa olleita ehtoja.',
    'terms.section2.title': '2. Palvelun käyttö ja velvollisuudet',
    'terms.section2.intro': 'Asiakas sitoutuu käyttämään palveluitamme ja verkkosivustoamme:',
    'terms.section2.item1': 'Voimassa olevan Suomen lainsäädännön ja EU-säädösten mukaisesti',
    'terms.section2.item2': 'Näiden käyttöehtojen ja mahdollisten erillisten sopimusten mukaisesti',
    'terms.section2.item3': 'Hyvän liiketavan ja ammattietiikan mukaisesti',
    'terms.section2.item4': 'Kunnioittaen muiden käyttäjien, kolmansien osapuolten ja yrityksen oikeuksia',
    'terms.section2.item5': 'Oikeellisin ja ajantasaisin tiedoin',
    'terms.section2.p2': 'Asiakas vastaa kaikista toimistaan ja laiminlyönneistään palveluita käyttäessään. Asiakas sitoutuu korvaamaan yritykselle kaikki vahingot, jotka aiheutuvat näiden ehtojen tai sovellettavan lainsäädännön vastaisesta toiminnasta.',
    'terms.section3.title': '3. Tilaukset, tarjoukset ja sopimukset',
    'terms.section3.intro': 'Tilausten ja palveluiden toimitus perustuu seuraaviin periaatteisiin:',
    'terms.section3.item1': 'Kaikki tarjouksemme ovat voimassa 30 päivää antamispäivästä, ellei toisin mainita',
    'terms.section3.item2': 'Tarjouksen hyväksyminen muodostaa sitovan sopimuksen osapuolten välille',
    'terms.section3.item3': 'Pidätämme oikeuden kieltäytyä tilauksen vastaanottamisesta ilman perusteluja',
    'terms.section3.item4': 'Toimitusajat ovat arvioita ja voivat vaihdella olosuhteiden mukaan',
    'terms.section3.item5': 'Sopimukseen sovelletaan tilaushetkellä voimassa olevia hintoja ja ehtoja',
    'terms.section3.item6': 'Kirjalliset sopimukset ja vahvistukset syrjäyttävät suulliset sopimukset',
    'terms.section3.p2': 'Pidätämme oikeuden tehdä kohtuullisia muutoksia toimituksiin, jos ne johtuvat teknisistä syistä, materiaalien saatavuudesta tai muista vastaavista perustelluista syistä. Merkittävistä muutoksista ilmoitetaan asiakkaalle viipymättä.',
    'terms.section4.title': '4. Hinnoittelu ja arvonlisävero',
    'terms.section4.intro': 'Hinnoittelussa noudatetaan seuraavia periaatteita:',
    'terms.section4.item1': 'Hintoihin lisätään kulloinkin voimassa oleva arvonlisävero (ALV), ellei toisin mainita',
    'terms.section4.item2': 'Hinnat ovat voimassa tarjouksen tai tilausvahvistuksen antamishetkellä',
    'terms.section4.item3': 'Pidätämme oikeuden muuttaa hintoja ilman ennakkoilmoitusta uusissa tilauksissa',
    'terms.section4.item4': 'Olemassa oleviin tilauksiin sovelletaan tilaushetkellä sovittuja hintoja',
    'terms.section4.item5': 'Mahdolliset toimitus-, asennus- ja muut lisäkustannukset ilmoitetaan erikseen',
    'terms.section4.p2': 'Mikäli arvonlisäverokannat muuttuvat tilauksen ja toimituksen välisenä aikana, sovelletaan toimitushetken verokantoja. Hinnanmuutoksista johtuen asiakas voi peruuttaa tilauksen ilman kustannuksia, mikäli hinnanmuutos on yli 10%.',
    'terms.section5.title': '5. Maksu ja laskutus',
    'terms.section5.intro': 'Maksuehdot ja -käytännöt:',
    'terms.section5.consumer': 'Kuluttaja-asiakkaat (potkurikorjaus):',
    'terms.section5.consumerText': 'Kuluttaja-asiakkaat maksavat työt etukäteen ennen työn aloittamista. Maksu voidaan suorittaa pankkisiirrolla tai kortilla. Työtä ei aloiteta ennen maksun vastaanottamista.',
    'terms.section5.business': 'Yritysasiakkaat:',
    'terms.section5.businessText': 'Yritysasiakkaille maksuehto on 7 päivää netto laskun päivämäärästä, ellei toisin sovita. Ensimmäisessä tilauksessa voidaan edellyttää ennakkomaksua.',
    'terms.section5.item1': 'Viivästyneestä maksusta peritään viivästyskorkoa korkolain mukaisesti',
    'terms.section5.item2': 'Pidätämme omistusoikeuden tuotteisiin kunnes maksu on suoritettu kokonaisuudessaan',
    'terms.section5.item3': 'Maksamattomat laskut siirretään perintään kuluttajansuojalain mukaisesti',
    'terms.section5.item4': 'Perintäkulut ja oikeudenkäyntikulut ovat asiakkaan vastuulla',
    'terms.section5.item5': 'Hyväksymme maksutapoina pankkisiirron, korttimaksun ja laskun (yritysasiakkaat)',
    'terms.section5.p2': 'Mikäli asiakas laiminlyö maksun, meillä on oikeus keskeyttää työ ja/tai periä koko jäljellä oleva summa välittömästi. Maksuviivästys oikeuttaa meidät purkamaan sopimuksen ja vaatimaan korvauksia.',
    'terms.section6.title': '6. Takuu ja virhevastuu',
    'terms.section6.intro': 'Takuuehdot ja vastuunrajoitukset:',
    'terms.section6.item1': 'Tarjoamme 12 kuukauden rajoitetun takuun valmistus- ja asennusvirheille',
    'terms.section6.item2': 'Takuu edellyttää tuotteen/palvelun käyttöä normaaliolosuhteissa tarkoituksenmukaisesti',
    'terms.section6.item3': 'Takuu ei koske normaalia kulumista, väärinkäytöstä tai vahingosta johtuvia vaurioita',
    'terms.section6.item4': 'Takuukorjaukset suoritetaan kohtuullisessa ajassa työjärjestyksemme mukaisesti',
    'terms.section6.item5': 'Vastaamme tuotteidemme ja palveluidemme kuluttajansuojalain mukaisesta virhevastuusta',
    'terms.section6.item6': 'Välilliset vahingot (esim. tuotannon keskeytyminen, ansionmenetys) eivät sisälly vastuuseemme',
    'terms.section6.item7': 'Korvausvastuumme enimmäismäärä rajoittuu tilauksen arvoon',
    'terms.section6.p2': 'Yritysasiakkaiden osalta vastaamme vain välittömistä vahingoista, jotka johtuvat törkeästä huolimattomuudesta tai tahallisesta toiminnasta. Kuluttaja-asiakkaisiin sovelletaan kuluttajansuojalain pakottavia säännöksiä.',
    'terms.section7.title': '7. Immateriaalioikeudet ja luottamuksellisuus',
    'terms.section7.p1': 'Kaikki verkkosivuston sisältö, mukaan lukien mutta ei rajoittuen tekstit, kuvat, grafiikat, logot, tietokantasisältö, suunnitelmat, piirustukset ja ohjelmistokoodit, ovat yrityksen tai sen lisenssinantajien omaisuutta ja suojattu tekijänoikeus-, tavaramerkki- ja muilla immateriaalioikeuslaeilla.',
    'terms.section7.intro': 'Sisältöä ei saa:',
    'terms.section7.item1': 'Kopioida, jäljentää tai levittää ilman kirjallista lupaa',
    'terms.section7.item2': 'Muokata, muuntaa tai luoda johdannaisteoksia',
    'terms.section7.item3': 'Käyttää kaupallisiin tarkoituksiin ilman lupaa',
    'terms.section7.item4': 'Poistaa tekijänoikeus- tai tavaramerkkimerkintöjä',
    'terms.section7.p2': 'Asiakas myöntää yritykselle ei-yksinoikeudellisen, rojaltivapaan lisenssin käyttää ja hyödyntää asiakkaan toimittamia tietoja palveluiden toimittamiseksi. Molemmat osapuolet sitoutuvat pitämään luottamuksellisina kaikki liiketoimintaan liittyvät tiedot.',
    'terms.section8.title': '8. Peruutus- ja palautusoikeus',
    'terms.section8.intro': 'Kuluttajilla on 14 päivän peruutusoikeus kuluttajansuojalain mukaisesti, ellei kyse ole:',
    'terms.section8.item1': 'Räätälöidyistä tai asiakkaan spesifikaation mukaan valmistetuista tuotteista',
    'terms.section8.item2': 'Palveluista, jotka on suoritettu kokonaan kuluttajan nimenomaisella suostumuksella',
    'terms.section8.item3': 'Kiireellisistä korjaus- tai huoltotöistä, jotka kuluttaja on nimenomaisesti pyytänyt',
    'terms.section8.item4': 'Pilaantuvista tai nopeasti vanhenevista tuotteista',
    'terms.section8.p2': 'Yritysasiakkailla ei ole lakisääteistä peruutusoikeutta. Peruutuksen yhteydessä palautetaan jo maksetut suoritukset, vähennettynä kohtuulliset kulut jo tehdystä työstä tai hankituista materiaaleista.',
    'terms.section8.urgentCancel': 'Kiireellisten korjaustöiden peruutus: Mikäli asiakas peruuttaa sovitun kiireellisen korjaustyön alle 48 tuntia ennen sovittua aloitusajankohtaa, veloitamme 25% kokonaiskustannuksista, kuitenkin vähintään 50 € + ALV. Peruutusmaksu kattaa menetettyjä työtunteja ja suunnittelutyötä.',
    'terms.section9.title': '9. Reklamaatiot ja virhevastuun vanhentuminen',
    'terms.section9.intro': 'Reklamaatioaikoja ja -menettelyä koskevat seuraavat ehdot:',
    'terms.section9.consumer': 'Kuluttajat:',
    'terms.section9.consumerText': 'Virheestä on reklamoitava kohtuullisessa ajassa virheen havaitsemisesta. Suosittelemme reklamoimaan viimeistään 2 viikon kuluessa virheen havaitsemisesta. Kuluttajansuojalain mukaiset vanhentumisajat pysyvät voimassa.',
    'terms.section9.business': 'Yritysasiakkaat:',
    'terms.section9.businessText': 'Reklamaation on tapahduttava viimeistään 1 kuukauden kuluessa virheen havaitsemisesta tai siitä, kun virhe olisi pitänyt havaita. Reklamaatio on tehtävä kirjallisesti ja siinä on yksilöitävä virhe riittävän tarkasti.',
    'terms.section9.p2': 'Reklamaatiot tulee osoittaa: innovation@fennofuture.fi tai +358 400 416 466. Vastaamme reklamaatioihin viipymättä ja pyrimme ratkaisemaan asian 14 päivän kuluessa.',
    'terms.section10.title': '10. Ylivoimainen este (Force Majeure)',
    'terms.section10.intro': 'Emme vastaa viivästyksistä, vahingosta tai muusta sopimusrikkomuksesta, joka aiheutuu ylivoimaisesta esteestä, kuten:',
    'terms.section10.item1': 'Luonnonkatastrofit (tulva, myrsky, maanjäristys)',
    'terms.section10.item2': 'Sota, mellakat, terrori-iskut',
    'terms.section10.item3': 'Lakot, työsulut tai muut työtaistelutoimenpiteet',
    'terms.section10.item4': 'Viranomaistoimenpiteet tai lainsäädännön muutokset',
    'terms.section10.item5': 'Energia- tai raaka-ainepula',
    'terms.section10.item6': 'Tulipalo, räjähdys tai muu vastaava vahinkotapahtuma',
    'terms.section10.item7': 'Alihankkijan tai toimittajan viivästys ylivoimaisen esteen vuoksi',
    'terms.section10.item8': 'Pandemiat, epidemiat tai muut poikkeukselliset terveysriskit',
    'terms.section10.p2': 'Ylivoimaisen esteen sattuessa ilmoitamme siitä asiakkaalle viipymättä ja pyrimme minimoimaan haitat. Mikäli ylivoimainen este kestää yli 3 kuukautta, kummallakin osapuolella on oikeus purkaa sopimus ilman korvausvelvollisuutta.',
    'terms.section11.title': '11. Tietosuoja ja henkilötietojen käsittely',
    'terms.section11.text': 'Käsittelemme henkilötietoja EU:n yleisen tietosuoja-asetuksen (GDPR) ja Suomen tietosuojalainsäädännön mukaisesti. Yksityiskohtaiset tiedot henkilötietojen käsittelystä löytyvät erillisestä',
    'terms.section11.link': 'tietosuojaselosteestamme',
    'terms.section12.title': '12. Muutokset ehtoihin',
    'terms.section12.text': 'Pidätämme oikeuden muuttaa näitä ehtoja. Muutoksista ilmoitetaan verkkosivustollamme vähintään 30 päivää ennen niiden voimaantuloa. Merkittävistä muutoksista ilmoitamme myös sähköpostitse rekisteröityneille asiakkaille. Olemassa oleviin sopimuksiin sovelletaan sopimushetkellä voimassa olleita ehtoja, ellei lainsäädäntö toisin edellytä. Jatkamalla palveluidemme käyttöä muutosten voimaantulon jälkeen hyväksyt muutetut ehdot.',
    'terms.section13.title': '13. Sovellettava laki ja riitojen ratkaiseminen',
    'terms.section13.p1': 'Näihin ehtoihin ja niiden tulkintaan sovelletaan Suomen lakia, poislukien lainvalintaa koskevat säännökset. Viittaus Suomen lakiin sisältää myös sovellettavat EU-säädökset.',
    'terms.section13.p2': 'Erimielisyydet pyritään ratkaisemaan ensisijaisesti neuvottelemalla osapuolten välillä. Mikäli sovintoa ei saavuteta 30 päivän kuluessa, riita ratkaistaan ensiasteessa Pirkanmaan käräjäoikeudessa Tampereella.',
    'terms.section13.p3': 'Kuluttajilla on oikeus saattaa riita-asia käsiteltäväksi myös kuluttajariitalautakunnassa (www.kuluttajariita.fi) tai EU:n kuluttajariidanratkaisualustalla (ec.europa.eu/consumers/odr). Kuluttaja voi nostaa kanteen myös oman asuinpaikkansa käräjäoikeudessa.',
    'terms.section14.title': '14. Yhteystiedot ja asiakaspalvelu',
    'terms.section14.businessId': 'Y-tunnus',
    'terms.section14.address': 'Osoite',
    'terms.section14.email': 'Sähköposti',
    'terms.section14.phone': 'Puhelin',
    'terms.section14.customerService': 'Asiakaspalvelu: Ma-Pe 8:00-16:00',
    'terms.updated': 'Päivitetty',
    'terms.version': 'Versio',

    // Privacy Page
    'privacy.title': 'Tietosuojaseloste',
    'privacy.subtitle': 'Potkurikorjaus - Henkilötietojen käsittely',
    'privacy.section1.title': '1. Rekisterinpitäjä',
    'privacy.section1.businessId': 'Y-tunnus',
    'privacy.section1.email': 'Sähköposti',
    'privacy.section1.phone': 'Puhelin',
    'privacy.section2.title': '2. Rekisterin nimi',
    'privacy.section2.text': 'FennoFuture asiakasrekisteri ja verkkosivuston käyttäjärekisteri',
    'privacy.section3.title': '3. Henkilötietojen käsittelyn tarkoitus',
    'privacy.section3.intro': 'Käsittelemme henkilötietoja seuraaviin tarkoituksiin:',
    'privacy.section3.item1': 'Asiakassuhteen hoitaminen ja ylläpito',
    'privacy.section3.item2': 'Tarjousten ja tilausten käsittely',
    'privacy.section3.item3': 'Laskutus ja maksuliikenne',
    'privacy.section3.item4': 'Yhteydenottoihin vastaaminen',
    'privacy.section3.item5': 'Palveluiden kehittäminen',
    'privacy.section3.item6': 'Markkinointi (suostumuksen perusteella)',
    'privacy.section4.title': '4. Rekisterin tietosisältö',
    'privacy.section4.intro': 'Rekisteri voi sisältää seuraavia tietoja:',
    'privacy.section4.item1': 'Nimi',
    'privacy.section4.item2': 'Yhteystiedot (osoite, puhelinnumero, sähköposti)',
    'privacy.section4.item3': 'Yrityksen nimi ja Y-tunnus',
    'privacy.section4.item4': 'Laskutustiedot',
    'privacy.section4.item5': 'Asiakassuhteeseen liittyvät tiedot',
    'privacy.section4.item6': 'Verkkosivuston käyttötiedot (evästeet, IP-osoite)',
    'privacy.section5.title': '5. Tietojen säilytysaika',
    'privacy.section5.text': 'Säilytämme henkilötietoja niin kauan kuin se on tarpeen käsittelyn tarkoituksen toteuttamiseksi tai lain edellyttämän ajan. Asiakastiedot säilytetään vähintään kirjanpitolain edellyttämän ajan (6 vuotta).',
    'privacy.section6.title': '6. Tietojen luovutus',
    'privacy.section6.intro': 'Emme luovuta henkilötietoja kolmansille osapuolille ilman suostumustanne, paitsi:',
    'privacy.section6.item1': 'Lakisääteisen velvollisuuden täyttämiseksi',
    'privacy.section6.item2': 'Palvelun toimittamiseen tarvittaville yhteistyökumppaneille (esim. maksupalvelut)',
    'privacy.section6.item3': 'Viranomaisten pyynnöstä lain edellyttämissä tapauksissa',
    'privacy.section7.title': '7. Rekisteröidyn oikeudet',
    'privacy.section7.intro': 'Teillä on seuraavat GDPR:n mukaiset oikeudet henkilötietojenne käsittelyyn liittyen:',
    'privacy.section7.access.title': 'Tarkastusoikeus (Oikeus saada pääsy tietoihin)',
    'privacy.section7.access.text': 'Teillä on oikeus saada tieto siitä, käsitelläänkö henkilötietojanne, ja jos käsitellään, oikeus saada pääsy henkilötietoihin sekä jäljennös käsiteltävistä henkilötiedoista. Voitte pyytää tietoa käsittelyn tarkoituksesta, tietoluokista, vastaanottajista ja säilytysajoista.',
    'privacy.section7.rectification.title': 'Oikeus tietojen oikaisemiseen',
    'privacy.section7.rectification.text': 'Jos henkilötietonne ovat virheellisiä tai puutteellisia, teillä on oikeus vaatia niiden oikaisemista tai täydentämistä. Pyrimme korjaamaan virheet viipymättä, viimeistään kuukauden kuluessa pyynnöstä.',
    'privacy.section7.erasure.title': 'Oikeus tietojen poistamiseen ("Oikeus tulla unohdetuksi")',
    'privacy.section7.erasure.text1': 'Teillä on oikeus pyytää henkilötietojenne poistamista tietyissä tilanteissa, esimerkiksi silloin kun tietoja ei enää tarvita niihin tarkoituksiin, joihin ne on kerätty, tai jos peruutatte antamanne suostumuksen.',
    'privacy.section7.erasure.text2': 'Tärkeä rajoitus: Emme voi poistaa henkilötietoja, joiden säilyttäminen on lakisääteisesti velvoitettua. Kirjanpitolain mukaan liiketapahtumia koskevia tietoja (kuten laskuja, sopimuksia ja maksutositteita) on säilytettävä vähintään kuusi (6) vuotta tilikauden päättymisestä. Näitä tietoja ei voida poistaa ennen lakisääteisen säilytysajan päättymistä, vaikka poistamista pyydettäisiin. Tällaisissa tapauksissa rajoitamme tietojen käsittelyn ainoastaan lakisääteiseen säilyttämiseen.',
    'privacy.section7.restriction.title': 'Oikeus käsittelyn rajoittamiseen',
    'privacy.section7.restriction.text': 'Teillä on oikeus vaatia henkilötietojenne käsittelyn rajoittamista tietyissä tilanteissa, esimerkiksi jos kiistätte tietojen paikkansapitävyyden tai vastustatte käsittelyä. Rajoittamisen aikana tietoja säilytetään, mutta niitä ei käsitellä muutoin.',
    'privacy.section7.objection.title': 'Vastustamisoikeus',
    'privacy.section7.objection.text': 'Teillä on oikeus henkilökohtaiseen erityiseen tilanteeseenne liittyvällä perusteella vastustaa henkilötietojenne käsittelyä, kun käsittely perustuu yleiseen etuun tai oikeutettuun etuumme. Tällöin arvioimme, onko meillä perusteltuja syitä käsittelyyn, jotka syrjäyttävät rekisteröidyn edut ja oikeudet.',
    'privacy.section7.portability.title': 'Oikeus siirtää tiedot järjestelmästä toiseen',
    'privacy.section7.portability.text': 'Teillä on oikeus saada henkilötietonne jäsennellyssä, yleisesti käytetyssä ja koneellisesti luettavassa muodossa ja oikeus siirtää tiedot toiselle rekisterinpitäjälle, jos käsittely perustuu suostumukseen tai sopimukseen ja käsittely tapahtuu automaattisesti.',
    'privacy.section7.withdraw.title': 'Oikeus peruuttaa suostumus',
    'privacy.section7.withdraw.text': 'Jos henkilötietojenne käsittely perustuu suostumukseenne, teillä on oikeus peruuttaa suostumuksenne milloin tahansa. Suostumuksen peruuttaminen ei vaikuta ennen peruuttamista suostumuksen perusteella suoritetun käsittelyn lainmukaisuuteen.',
    'privacy.section7.complaint.title': 'Oikeus tehdä valitus valvontaviranomaiselle',
    'privacy.section7.complaint.text': 'Teillä on oikeus tehdä valitus toimivaltaiselle valvontaviranomaiselle (Suomessa Tietosuojavaltuutetun toimisto, www.tietosuoja.fi), jos katsotte, että henkilötietojenne käsittelyssä rikotaan tietosuoja-asetusta.',
    'privacy.section7.exercise.title': 'Oikeuksien käyttäminen:',
    'privacy.section7.exercise.intro': 'Voitte käyttää oikeuksianne ottamalla yhteyttä:',
    'privacy.section7.exercise.email': 'Sähköposti',
    'privacy.section7.exercise.phone': 'Puhelin',
    'privacy.section7.exercise.address': 'Postiosoite: FennoFuture, Turuntie 3, 37830 Akaa',
    'privacy.section7.exercise.response': 'Vastaamme pyyntöihinne pääsääntöisesti kuukauden kuluessa. Monimutkaisissa tapauksissa voimme pidentää määräaikaa kahdella kuukaudella, josta ilmoitamme teille erikseen.',
    'privacy.section8.title': '8. Tietojen suojaus',
    'privacy.section8.text': 'Suojaamme henkilötietoja asianmukaisesti teknisillä ja organisatorisilla turvatoimilla. Tietoja käsitellään luottamuksellisesti ja vain siihen oikeutetut henkilöt pääsevät niihin käsiksi.',
    'privacy.section9.title': '9. Evästeet',
    'privacy.section9.text': 'Verkkosivustomme voi käyttää evästeitä käyttökokemuksen parantamiseksi. Voit hallita evästeiden käyttöä selaimesi asetuksista.',
    'privacy.section10.title': '10. Muutokset tietosuojaselosteeseen',
    'privacy.section10.text': 'Pidätämme oikeuden päivittää tätä tietosuojaselostetta. Muutoksista ilmoitetaan verkkosivustollamme.',
    'privacy.updated': 'Päivitetty',
  },

  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.pricing': 'Pricing',
    'nav.faq': 'FAQ',
    'nav.shipping': 'Shipping',
    'nav.contact': 'Contact',
    'nav.callNow': 'Call Now',

    // Homepage Hero
    'home.hero.badge': 'Propeller repair and spare parts',
    'home.hero.title': 'Reliable Propeller Repair',
    'home.hero.titleHighlight': 'in Pirkanmaa',
    'home.hero.subtitle': 'We repair propellers professionally. Contact us and tell us your needs!',
    'home.hero.cta.message': 'Send message',
    'home.hero.cta.call': 'Call +358 400 416 466',

    // Homepage Features
    'home.features.quality.title': 'Quality before speed',
    'home.features.quality.desc': 'Careful work',
    'home.features.warranty.title': '12 month warranty',
    'home.features.warranty.desc': 'On all repairs',
    'home.features.satisfaction.title': 'Satisfaction guaranteed',
    'home.features.satisfaction.desc': 'Aiming for functionality',
    'home.features.experience.title': '100+ repairs',
    'home.features.experience.desc': 'Diverse experience',

    // Homepage Services Preview
    'home.services.title': 'Our Services',
    'home.services.subtitle': 'Comprehensive propeller repair and maintenance services',
    'home.services.repair.title': 'Propeller Repairs',
    'home.services.repair.desc': 'Bent blade straightening, crack welding, damage repair',
    'home.services.repair.price': 'From €95',
    'home.services.maintenance.title': 'Maintenance & Restoration',
    'home.services.maintenance.desc': 'Balancing, polishing, coating, antifouling treatment',
    'home.services.maintenance.price': 'From €80',
    'home.services.parts.title': 'Spare Parts',
    'home.services.parts.desc': 'Propellers, engine parts, maintenance parts and more',
    'home.services.parts.price': 'Ask for quote',
    'home.services.viewAll': 'View all services',
    'home.services.inspection.title': 'Inspection',
    'home.services.inspection.desc': 'Thorough condition assessment and documentation',

    // Homepage Process
    'home.process.title': 'How the repair process works',
    'home.process.subtitle': '7 clear steps',
    'home.process.step1.title': '1. Contact',
    'home.process.step1.desc': 'Contact us by phone, email or use the calculator for price estimate.',
    'home.process.step2.title': '2. Propeller delivery',
    'home.process.step2.desc': 'Bring propeller to our location, send by mail or order pickup service.',
    'home.process.step3.title': '3. Inspection',
    'home.process.step3.desc': 'We thoroughly inspect the propeller and document the damage.',
    'home.process.step4.title': '4. Quote',
    'home.process.step4.desc': 'We contact you and inform you of the price.',
    'home.process.step5.title': '5. Repair',
    'home.process.step5.desc': 'After approved quote, we start the repair professionally.',
    'home.process.step6.title': '6. Quality check',
    'home.process.step6.desc': 'We check work quality, balance and test.',
    'home.process.step7.title': '7. Delivery',
    'home.process.step7.desc': 'We notify completion. Pickup or we deliver.',

    // Homepage CTA
    'home.cta.title': 'Need propeller repair?',
    'home.cta.subtitle': 'Calculate price with free calculator or contact us directly.',
    'home.cta.button': 'Contact us',

    // Homepage Why Choose Us
    'home.why.title': 'Why Choose Us?',
    'home.why.warranty.title': 'Warranty',
    'home.why.warranty.desc': '12-month warranty on all work',
    'home.why.expertise.title': 'Expertise',
    'home.why.expertise.desc': 'Experience in diverse repairs',
    'home.why.quality.title': 'Quality',
    'home.why.quality.desc': 'We use quality materials and methods',
    'home.why.flexibility.title': 'Flexibility',
    'home.why.flexibility.desc': 'Also rush jobs and express service',
    'home.why.service.title': 'Customer Service',
    'home.why.service.desc': 'Personal service in Finnish and English',
    'home.why.pricing.title': 'Transparent Pricing',
    'home.why.pricing.desc': 'Clear prices without surprises',

    // Services Page
    'services.title': 'Our Services',
    'services.subtitle': 'Comprehensive propeller repair and maintenance services. Expertise and quality.',
    'services.repair.title': 'Propeller Repairs',
    'services.repair.desc': 'Professional repair service for all propeller types',
    'services.repair.feature1': 'Bent blade straightening',
    'services.repair.feature2': 'Crack welding',
    'services.repair.feature3': 'Chip repair',
    'services.repair.feature4': 'Crack repairs',
    'services.repair.feature5': 'Cavitation damage repair',
    'services.maintenance.title': 'Maintenance & Restoration',
    'services.maintenance.desc': 'Regular maintenance extends propeller lifespan',
    'services.maintenance.feature1': 'Balancing',
    'services.maintenance.feature2': 'Polishing',
    'services.maintenance.feature3': 'Coating',
    'services.maintenance.feature4': 'Antifouling treatment',
    'services.maintenance.feature5': 'Bushing replacement',
    'services.parts.title': 'Spare Parts',
    'services.parts.desc': 'Propellers, engine parts, maintenance parts and more',
    'services.parts.feature1': 'Propellers for all engines',
    'services.parts.feature2': 'Engine parts',
    'services.parts.feature3': 'Maintenance parts',
    'services.parts.feature4': 'Installation available',
    'services.parts.feature5': 'Advice and recommendations',
    'services.inspection.title': 'Inspection and Damage Reports',
    'services.inspection.desc': 'Thorough condition assessment and documentation',
    'services.inspection.feature1': 'Visual inspection',
    'services.inspection.feature2': 'Measurements',
    'services.inspection.feature3': 'Damage mapping',
    'services.inspection.feature4': 'Written report',
    'services.inspection.feature5': 'Repair recommendation',
    'services.emergency.title': 'Express Service',
    'services.emergency.desc': 'Urgent repairs with fast turnaround',
    'services.emergency.feature1': 'Rush repairs',
    'services.emergency.feature2': 'Temporary solutions',
    'services.emergency.feature3': 'Pickup and delivery',
    'services.emergency.feature4': 'Spare propellers',
    'services.emergency.feature5': 'Priority scheduling',
    'services.whyChoose': 'Why Choose Us?',
    'services.from': 'From',
    'services.askQuote': 'Ask for quote',
    'services.repair.price': 'From €95',
    'services.maintenance.price': 'From €50',
    'services.inspection.price': 'From €30',
    'services.emergency.price': '0-24h: +100%',

    // Pricing Page
    'pricing.title': 'Pricing',
    'pricing.subtitle': 'Select options below and calculate estimated price',
    'pricing.material': 'Material',
    'pricing.material.note': 'In some cases a new propeller may be a more affordable option.',
    'pricing.material.titanium': 'Titanium',
    'pricing.material.titanium.desc': 'Premium material for special use',
    'pricing.material.bronze': 'Bronze',
    'pricing.material.bronze.desc': 'Traditional for marine use',
    'pricing.material.stainless': 'Stainless Steel',
    'pricing.material.stainless.desc': 'Durable and strong material',
    'pricing.material.aluminum': 'Aluminium',
    'pricing.material.aluminum.desc': 'Light and common material',
    'pricing.size': 'Propeller Size',
    'pricing.size.basic': 'Basic size',
    'pricing.size.medium': 'Medium size',
    'pricing.size.large': 'Large propellers',
    'pricing.size.xlarge': 'Extra large',
    'pricing.size.professional': 'Professional use',
    'pricing.duoprop': 'Duoprop (twin propeller)',
    'pricing.services': 'Additional Services',
    'pricing.services.duopropNote': 'Duoprop selected: marked services x2',
    'pricing.services.balancing': 'Balancing',
    'pricing.services.balancing.desc': 'Reduces vibration',
    'pricing.services.polishing': 'Polishing',
    'pricing.services.polishing.desc': 'Improves efficiency',
    'pricing.services.coating': 'Coating',
    'pricing.services.coating.desc': 'Protects from corrosion',
    'pricing.services.bushing': 'Bushing replacement',
    'pricing.services.bushing.desc': 'Replacement work',
    'pricing.services.documentation': 'Documentation',
    'pricing.services.documentation.desc': 'Repair report',
    'pricing.services.inspection': 'Inspection fee',
    'pricing.services.inspection.desc': 'If repair is not done',
    'pricing.services.shipping': 'Shipping',
    'pricing.services.shipping.desc': 'Return freight to customer',
    'pricing.urgency': 'Timeline',
    'pricing.urgency.normal': 'Normal',
    'pricing.urgency.normal.time': 'According to work queue',
    'pricing.urgency.urgent': 'Urgent',
    'pricing.urgency.urgent.time': '2-4 days',
    'pricing.urgency.veryUrgent': 'Very urgent',
    'pricing.urgency.veryUrgent.time': '1-2 days',
    'pricing.urgency.emergency': 'Emergency',
    'pricing.urgency.emergency.time': 'Same day',
    'pricing.estimate': 'Price Estimate',
    'pricing.estimate.total': 'Estimated total price',
    'pricing.estimate.repair': 'Repair work',
    'pricing.estimate.services': 'Additional services',
    'pricing.estimate.vat': 'VAT 25.5% included. Final price confirmed after inspection.',
    'pricing.estimate.inspectionVat': 'Charged if repair is not done. VAT 25.5% included.',
    'pricing.estimate.select': 'Select material and size to see price estimate',
    'pricing.sendQuote': 'Send quote request',
    'pricing.help.title': 'Need help with pricing?',
    'pricing.help.subtitle': 'Contact us and we will help you',
    'pricing.help.call': 'Call',
    'pricing.common': 'Common',
    'pricing.starting': 'From',
    'pricing.blades': 'Number of Blades',
    'pricing.blades.label': 'blades',
    'pricing.blades.2': '2',
    'pricing.blades.3': '3',
    'pricing.blades.4': '4',
    'pricing.blades.5': '5',
    'pricing.blades.6plus': '6+',

    // Contact Page
    'contact.title': 'Contact Us',
    'contact.subtitle': 'We are happy to help with all propeller repair questions.',
    'contact.info': 'Contact Information',
    'contact.phone': 'Phone',
    'contact.phone.hours': 'Mon-Fri 8-16',
    'contact.email': 'Email',
    'contact.email.response': 'We respond within 24h',
    'contact.location': 'Location',
    'contact.location.area': 'Service area: Akaa / Sastamala / Urjala',
    'contact.hours': 'Opening Hours',
    'contact.hours.weekday': 'Monday - Friday: 8:00 - 16:00',
    'contact.hours.weekend': 'Weekend: By appointment',
    'contact.questions': 'Questions?',
    'contact.questions.desc': 'Call us and we will figure out how we can help.',
    'contact.form.title': 'Send Message',
    'contact.form.estimate': 'Price estimate from pricing page',
    'contact.form.prefilled': 'Information is prefilled. Fill in your contact details and send quote request.',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Phone',
    'contact.form.propellerType': 'Propeller Type',
    'contact.form.propellerType.select': 'Select...',
    'contact.form.propellerType.aluminum': 'Aluminium',
    'contact.form.propellerType.stainless': 'Stainless Steel',
    'contact.form.propellerType.bronze': 'Bronze',
    'contact.form.propellerType.other': 'Other propeller type',
    'contact.form.duoprop': 'Duoprop',
    'contact.form.priority': 'Urgency / Priority',
    'contact.form.priority.normal': 'Normal',
    'contact.form.priority.normal.desc': 'No rush',
    'contact.form.priority.moderate': 'Moderate',
    'contact.form.priority.moderate.desc': '1-2 weeks',
    'contact.form.priority.urgent': 'Urgent',
    'contact.form.priority.urgent.desc': 'Under a week',
    'contact.form.priority.emergency': 'Emergency',
    'contact.form.priority.emergency.desc': 'Immediately',
    'contact.form.delivery': 'Delivery',
    'contact.form.delivery.bring': 'I will bring it myself',
    'contact.form.delivery.send': 'I will send by mail',
    'contact.form.delivery.pickup': 'I would like pickup service',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Tell us about the propeller damage and what repair you need...',
    'contact.form.images': 'Images (optional)',
    'contact.form.images.desc': 'Add images of the damage - max 5 images, 5 MB / image',
    'contact.form.images.add': 'Add images',
    'contact.form.images.addMore': 'Add more images',
    'contact.form.submit': 'Send Message',
    'contact.form.submitting': 'Sending...',
    'contact.form.success': 'Message sent! We will respond as soon as possible.',
    'contact.form.error': 'Failed to send message. Please try again or contact by phone.',
    'contact.form.validation.contact': 'Please provide either email or phone number (or both)',
    'contact.form.validation.tooFast': 'Form was filled too quickly. Please wait a moment and try again.',
    'contact.form.validation.rateLimit': 'Please wait {seconds} seconds before next submission.',
    'contact.form.validation.maxImages': 'You can add maximum {max} images',
    'contact.form.validation.imageType': 'Only image files allowed',
    'contact.form.validation.imageSize': 'Maximum image size is 5 MB',
    'contact.map': 'Location',

    // FAQ Page
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Answers to common questions about propeller repairs',
    'faq.q1': 'How much does propeller repair cost?',
    'faq.a1': 'Prices start from €95 for simple repairs. As this is handcraft work, the service may not be a sensible solution for everyone. The final price depends on the extent of damage, propeller size and required work. Use our free calculator to get an instant estimate or contact us for an accurate quote.',
    'faq.q2': 'How long does the repair take?',
    'faq.a2': 'Normal repairs are completed in 3-7 days. Please note that work queue length may affect delivery time. Complex repairs may require more time. We will inform you of the exact delivery time in the quote.',
    'faq.q3': 'What types of propellers do you repair?',
    'faq.a3': 'We mainly repair boat propellers made of aluminum, stainless steel and bronze alloys. We have also serviced some waterjet impellers.',
    'faq.q4': 'Do you offer pickup and delivery service?',
    'faq.a4': 'Yes. We offer pickup and delivery service for an additional fee. You can also send the propeller to us by mail or bring it to the workshop yourself. See more details on our Shipping page.',
    'faq.q5': 'Is there a warranty for repairs?',
    'faq.a5': 'Yes! All repairs come with a 12-month warranty. The warranty covers workmanship defects and material flaws. The warranty does not cover normal wear and tear or new damage.',
    'faq.q6': 'Can you repair a badly damaged propeller?',
    'faq.a6': 'In most cases, yes! We repair even badly damaged propellers: bent blades, cracks, chips, cavitation damage. We always first assess whether repair is economically sensible vs. a new propeller.',
    'faq.q7': 'Do you do urgent repairs?',
    'faq.a7': 'Yes! We offer express service for an additional fee. Call directly +358 400 416 466 if you need urgent repair. See more details on our Pricing page.',
    'faq.q8': 'How can I pay?',
    'faq.a8': 'We accept cash, bank transfer and invoice (business customers). Payment is made before propeller release. We send the invoice by email as soon as the work is complete. Cash payment is possible upon pickup, receipt will be sent by email.',
    'faq.noAnswer': 'Didn\'t find an answer?',
    'faq.noAnswerDesc': 'Contact us and we will answer your question personally.',

    // Shipping Page
    'shipping.title': 'Shipping & Logistics',
    'shipping.subtitle': 'Several ways to deliver the propeller to us and back.',
    'shipping.options': 'Delivery Options',
    'shipping.bringYourself': 'Bring yourself',
    'shipping.bringYourselfDesc': 'Fastest and easiest way. We work in a workshop where doors are locked and we are not always present. Visits are arranged in advance.',
    'shipping.free': 'Free',
    'shipping.byAppointment': 'By appointment Mon-Fri 8-16',
    'shipping.postIt': 'Mail to us',
    'shipping.postItDesc': 'Pack carefully and send via Matkahuolto/Post. Instructions below.',
    'shipping.ownCost': 'Your cost',
    'shipping.usually': 'Usually €10-25',
    'shipping.mostPopular': 'Most popular',
    'shipping.pickupService': 'Pickup service',
    'shipping.pickupServiceDesc': 'We pick up the propeller from you and deliver back when ready.',
    'shipping.perKm': '€1.21/km',
    'shipping.dependsOnDistance': 'Price depends on distance',
    'shipping.calculatePrice': 'Calculate price',
    'shipping.priceCalculator': 'Pickup service price calculator',
    'shipping.fromAddress': 'Starting address',
    'shipping.addressPlaceholder': 'Enter your address',
    'shipping.serviceType': 'Service type',
    'shipping.pickupOnly': 'Pickup only',
    'shipping.deliveryOnly': 'Delivery only',
    'shipping.pickupAndDelivery': 'Pickup and delivery',
    'shipping.oneWayDistance': 'One-way distance',
    'shipping.pricePerKm': 'Price per km',
    'shipping.estimatedPrice': 'Estimated price',
    'shipping.priceNote': 'Note: This is an estimate. Final price confirmed in quote.',
    'shipping.packagingInstructions': 'Packaging instructions for mailing',
    'shipping.packStep1': 'Dry the propeller before packing',
    'shipping.packStep2': 'Use a sturdy cardboard box or wooden crate',
    'shipping.packStep3': 'Fill empty spaces with newspaper, bubble wrap or similar',
    'shipping.packStep4': 'Include your contact details and brief description of damage',
    'shipping.pickupArea': 'Pickup service area',
    'shipping.pickupAreaDesc': 'We offer pickup service mainly in the Pirkanmaa region.',
    'shipping.deliveryTime': 'Return delivery time',
    'shipping.pickupTime': 'Pickup: Arranged separately',
    'shipping.postTime': 'Post: 1-3 business days',
    'shipping.needHelp': 'Need help with shipping?',
    'shipping.needHelpDesc': 'Call us and we will help you the best way.',

    // Footer
    'footer.services': 'Services',
    'footer.contact': 'Contact',
    'footer.hours': 'Opening Hours',
    'footer.weekdays': 'Mon-Fri: 8-16',
    'footer.weekend': 'Sat-Sun: By appointment',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.copyright': 'All rights reserved.',
    'footer.tagline': 'Professional propeller repair',
    'footer.description': 'We repair boat propellers professionally. Aluminum, steel and bronze propellers. 12-month warranty on all work.',
    'footer.businessId': 'Business ID',
    'footer.founded': 'Founded',
    'footer.quickLinks': 'Quick Links',
    'footer.madeWith': 'Made with',
    'footer.inFinland': 'in Finland',

    // Common
    'common.learnMore': 'Learn more',
    'common.callNow': 'Call Now',
    'common.from': 'From',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.notProvided': 'Not provided',

    // Terms Page
    'terms.title': 'Terms of Service',
    'terms.subtitle': 'Terms of use for the Propeller Repair website and services',
    'terms.section1.title': '1. General Terms and Scope',
    'terms.section1.p1': 'These general terms and conditions ("Terms") apply to the products and services offered by FennoFuture (Business ID 3203489-7, "Company", "we", "our") and the use of the website. By using our services or placing an order, you accept these terms and agree to comply with them.',
    'terms.section1.p2': 'These terms form a legally binding agreement between the customer and the company. We reserve the right to change these terms by notifying changes on our website at least 30 days before the changes take effect. Existing contracts are subject to the terms in force at the time of order.',
    'terms.section2.title': '2. Use of Service and Obligations',
    'terms.section2.intro': 'The customer agrees to use our services and website:',
    'terms.section2.item1': 'In accordance with applicable Finnish legislation and EU regulations',
    'terms.section2.item2': 'In accordance with these terms of use and any separate agreements',
    'terms.section2.item3': 'In accordance with good business practice and professional ethics',
    'terms.section2.item4': 'Respecting the rights of other users, third parties and the company',
    'terms.section2.item5': 'With accurate and up-to-date information',
    'terms.section2.p2': 'The customer is responsible for all their actions and omissions when using the services. The customer agrees to compensate the company for all damages resulting from actions contrary to these terms or applicable legislation.',
    'terms.section3.title': '3. Orders, Quotes and Contracts',
    'terms.section3.intro': 'Delivery of orders and services is based on the following principles:',
    'terms.section3.item1': 'All our quotes are valid for 30 days from the date of issue, unless otherwise stated',
    'terms.section3.item2': 'Acceptance of a quote forms a binding contract between the parties',
    'terms.section3.item3': 'We reserve the right to refuse to accept an order without justification',
    'terms.section3.item4': 'Delivery times are estimates and may vary depending on circumstances',
    'terms.section3.item5': 'The contract is subject to the prices and terms in force at the time of order',
    'terms.section3.item6': 'Written contracts and confirmations supersede verbal agreements',
    'terms.section3.p2': 'We reserve the right to make reasonable changes to deliveries if they are due to technical reasons, availability of materials or other similar justified reasons. Significant changes will be notified to the customer without delay.',
    'terms.section4.title': '4. Pricing and VAT',
    'terms.section4.intro': 'The following principles apply to pricing:',
    'terms.section4.item1': 'Prices are subject to the applicable value added tax (VAT), unless otherwise stated',
    'terms.section4.item2': 'Prices are valid at the time of the quote or order confirmation',
    'terms.section4.item3': 'We reserve the right to change prices without notice for new orders',
    'terms.section4.item4': 'Existing orders are subject to the prices agreed at the time of order',
    'terms.section4.item5': 'Any delivery, installation and other additional costs are stated separately',
    'terms.section4.p2': 'If VAT rates change between the order and delivery, the tax rates at the time of delivery apply. Due to price changes, the customer may cancel the order at no cost if the price change is more than 10%.',
    'terms.section5.title': '5. Payment and Invoicing',
    'terms.section5.intro': 'Payment terms and practices:',
    'terms.section5.consumer': 'Consumer customers (propeller repair):',
    'terms.section5.consumerText': 'Consumer customers pay for work in advance before work begins. Payment can be made by bank transfer or card. Work will not begin until payment has been received.',
    'terms.section5.business': 'Business customers:',
    'terms.section5.businessText': 'For business customers, the payment term is 7 days net from the date of invoice, unless otherwise agreed. Advance payment may be required for the first order.',
    'terms.section5.item1': 'Late payment is subject to interest in accordance with the Interest Act',
    'terms.section5.item2': 'We retain ownership of products until payment has been made in full',
    'terms.section5.item3': 'Unpaid invoices are transferred to collection in accordance with consumer protection law',
    'terms.section5.item4': 'Collection costs and legal costs are the responsibility of the customer',
    'terms.section5.item5': 'We accept bank transfer, card payment and invoice (business customers) as payment methods',
    'terms.section5.p2': 'If the customer fails to pay, we have the right to suspend work and/or collect the entire remaining amount immediately. Late payment entitles us to terminate the contract and claim compensation.',
    'terms.section6.title': '6. Warranty and Liability for Defects',
    'terms.section6.intro': 'Warranty terms and liability limitations:',
    'terms.section6.item1': 'We offer a 12-month limited warranty for manufacturing and installation defects',
    'terms.section6.item2': 'The warranty requires proper use of the product/service under normal conditions',
    'terms.section6.item3': 'The warranty does not cover normal wear, misuse or accidental damage',
    'terms.section6.item4': 'Warranty repairs are carried out within a reasonable time according to our work schedule',
    'terms.section6.item5': 'We are responsible for defect liability in accordance with consumer protection law',
    'terms.section6.item6': 'Indirect damages (e.g. production interruption, loss of income) are not included in our liability',
    'terms.section6.item7': 'Our maximum liability is limited to the value of the order',
    'terms.section6.p2': 'For business customers, we are only liable for direct damages resulting from gross negligence or intentional actions. Mandatory consumer protection provisions apply to consumer customers.',
    'terms.section7.title': '7. Intellectual Property Rights and Confidentiality',
    'terms.section7.p1': 'All website content, including but not limited to texts, images, graphics, logos, database content, designs, drawings and software code, are the property of the company or its licensors and are protected by copyright, trademark and other intellectual property laws.',
    'terms.section7.intro': 'Content may not be:',
    'terms.section7.item1': 'Copied, reproduced or distributed without written permission',
    'terms.section7.item2': 'Modified, transformed or used to create derivative works',
    'terms.section7.item3': 'Used for commercial purposes without permission',
    'terms.section7.item4': 'Have copyright or trademark notices removed',
    'terms.section7.p2': 'The customer grants the company a non-exclusive, royalty-free license to use and exploit the information provided by the customer to deliver the services. Both parties agree to keep all business-related information confidential.',
    'terms.section8.title': '8. Cancellation and Return Rights',
    'terms.section8.intro': 'Consumers have a 14-day cancellation right under consumer protection law, unless it concerns:',
    'terms.section8.item1': 'Customized or products made to customer specifications',
    'terms.section8.item2': 'Services that have been fully performed with the express consent of the consumer',
    'terms.section8.item3': 'Urgent repair or maintenance work expressly requested by the consumer',
    'terms.section8.item4': 'Perishable or rapidly expiring products',
    'terms.section8.p2': 'Business customers do not have a statutory right of cancellation. Upon cancellation, payments already made will be refunded, less reasonable costs for work already done or materials purchased.',
    'terms.section8.urgentCancel': 'Cancellation of urgent repairs: If the customer cancels an agreed urgent repair less than 48 hours before the agreed start time, we charge 25% of the total cost, but at least €50 + VAT. The cancellation fee covers lost work hours and planning work.',
    'terms.section9.title': '9. Complaints and Limitation of Defect Liability',
    'terms.section9.intro': 'The following terms apply to complaint periods and procedures:',
    'terms.section9.consumer': 'Consumers:',
    'terms.section9.consumerText': 'The defect must be complained about within a reasonable time of discovering the defect. We recommend complaining no later than 2 weeks after discovering the defect. Consumer protection limitation periods remain in force.',
    'terms.section9.business': 'Business customers:',
    'terms.section9.businessText': 'The complaint must be made no later than 1 month after discovering the defect or when the defect should have been discovered. The complaint must be made in writing and must specify the defect in sufficient detail.',
    'terms.section9.p2': 'Complaints should be addressed to: innovation@fennofuture.fi or +358 400 416 466. We respond to complaints without delay and aim to resolve the matter within 14 days.',
    'terms.section10.title': '10. Force Majeure',
    'terms.section10.intro': 'We are not liable for delays, damage or other breach of contract caused by force majeure, such as:',
    'terms.section10.item1': 'Natural disasters (flood, storm, earthquake)',
    'terms.section10.item2': 'War, riots, terrorist attacks',
    'terms.section10.item3': 'Strikes, lockouts or other industrial action',
    'terms.section10.item4': 'Government actions or changes in legislation',
    'terms.section10.item5': 'Energy or raw material shortages',
    'terms.section10.item6': 'Fire, explosion or other similar damage event',
    'terms.section10.item7': 'Delay of subcontractor or supplier due to force majeure',
    'terms.section10.item8': 'Pandemics, epidemics or other exceptional health risks',
    'terms.section10.p2': 'In the event of force majeure, we will notify the customer without delay and try to minimize the damage. If the force majeure lasts more than 3 months, either party has the right to terminate the contract without compensation.',
    'terms.section11.title': '11. Data Protection and Processing of Personal Data',
    'terms.section11.text': 'We process personal data in accordance with the EU General Data Protection Regulation (GDPR) and Finnish data protection legislation. Detailed information on the processing of personal data can be found in our separate',
    'terms.section11.link': 'privacy policy',
    'terms.section12.title': '12. Changes to Terms',
    'terms.section12.text': 'We reserve the right to change these terms. Changes will be notified on our website at least 30 days before they take effect. We will also notify registered customers of significant changes by email. Existing contracts are subject to the terms in force at the time of the contract, unless legislation requires otherwise. By continuing to use our services after changes take effect, you accept the amended terms.',
    'terms.section13.title': '13. Applicable Law and Dispute Resolution',
    'terms.section13.p1': 'These terms and their interpretation are governed by Finnish law, excluding conflict of law rules. Reference to Finnish law also includes applicable EU regulations.',
    'terms.section13.p2': 'Disputes will be settled primarily by negotiation between the parties. If no settlement is reached within 30 days, the dispute will be settled in the first instance by the Pirkanmaa District Court in Tampere.',
    'terms.section13.p3': 'Consumers have the right to bring the dispute to the Consumer Disputes Board (www.kuluttajariita.fi) or the EU Online Dispute Resolution platform (ec.europa.eu/consumers/odr). The consumer may also bring action in the district court of their own place of residence.',
    'terms.section14.title': '14. Contact Information and Customer Service',
    'terms.section14.businessId': 'Business ID',
    'terms.section14.address': 'Address',
    'terms.section14.email': 'Email',
    'terms.section14.phone': 'Phone',
    'terms.section14.customerService': 'Customer Service: Mon-Fri 8:00-16:00',
    'terms.updated': 'Updated',
    'terms.version': 'Version',

    // Privacy Page
    'privacy.title': 'Privacy Policy',
    'privacy.subtitle': 'Propeller Repair - Processing of Personal Data',
    'privacy.section1.title': '1. Data Controller',
    'privacy.section1.businessId': 'Business ID',
    'privacy.section1.email': 'Email',
    'privacy.section1.phone': 'Phone',
    'privacy.section2.title': '2. Name of Register',
    'privacy.section2.text': 'FennoFuture customer register and website user register',
    'privacy.section3.title': '3. Purpose of Processing Personal Data',
    'privacy.section3.intro': 'We process personal data for the following purposes:',
    'privacy.section3.item1': 'Managing and maintaining customer relationships',
    'privacy.section3.item2': 'Processing quotes and orders',
    'privacy.section3.item3': 'Invoicing and payment transactions',
    'privacy.section3.item4': 'Responding to inquiries',
    'privacy.section3.item5': 'Developing services',
    'privacy.section3.item6': 'Marketing (based on consent)',
    'privacy.section4.title': '4. Contents of the Register',
    'privacy.section4.intro': 'The register may contain the following information:',
    'privacy.section4.item1': 'Name',
    'privacy.section4.item2': 'Contact information (address, phone number, email)',
    'privacy.section4.item3': 'Company name and business ID',
    'privacy.section4.item4': 'Billing information',
    'privacy.section4.item5': 'Information related to customer relationship',
    'privacy.section4.item6': 'Website usage data (cookies, IP address)',
    'privacy.section5.title': '5. Data Retention Period',
    'privacy.section5.text': 'We retain personal data for as long as necessary to fulfill the purpose of processing or for the period required by law. Customer data is retained for at least the period required by the Accounting Act (6 years).',
    'privacy.section6.title': '6. Disclosure of Data',
    'privacy.section6.intro': 'We do not disclose personal data to third parties without your consent, except:',
    'privacy.section6.item1': 'To fulfill a legal obligation',
    'privacy.section6.item2': 'To partners necessary for providing the service (e.g. payment services)',
    'privacy.section6.item3': 'At the request of authorities in cases required by law',
    'privacy.section7.title': '7. Rights of the Data Subject',
    'privacy.section7.intro': 'You have the following rights under the GDPR regarding the processing of your personal data:',
    'privacy.section7.access.title': 'Right of Access',
    'privacy.section7.access.text': 'You have the right to know whether your personal data is being processed, and if so, the right to access the personal data and receive a copy of the personal data being processed. You may request information about the purpose of processing, data categories, recipients and retention periods.',
    'privacy.section7.rectification.title': 'Right to Rectification',
    'privacy.section7.rectification.text': 'If your personal data is incorrect or incomplete, you have the right to request its rectification or completion. We aim to correct errors without delay, within one month of the request at the latest.',
    'privacy.section7.erasure.title': 'Right to Erasure ("Right to be Forgotten")',
    'privacy.section7.erasure.text1': 'You have the right to request the erasure of your personal data in certain situations, for example when the data is no longer needed for the purposes for which it was collected, or if you withdraw your consent.',
    'privacy.section7.erasure.text2': 'Important limitation: We cannot delete personal data that we are legally required to retain. According to the Accounting Act, business transaction data (such as invoices, contracts and payment receipts) must be retained for at least six (6) years from the end of the financial year. This data cannot be deleted before the statutory retention period expires, even if deletion is requested. In such cases, we limit the processing of data to statutory retention only.',
    'privacy.section7.restriction.title': 'Right to Restriction of Processing',
    'privacy.section7.restriction.text': 'You have the right to request the restriction of processing of your personal data in certain situations, for example if you contest the accuracy of the data or object to processing. During restriction, data is stored but not otherwise processed.',
    'privacy.section7.objection.title': 'Right to Object',
    'privacy.section7.objection.text': 'You have the right to object to the processing of your personal data on grounds relating to your particular situation, when processing is based on public interest or our legitimate interest. We will then assess whether we have compelling legitimate grounds for processing that override the interests and rights of the data subject.',
    'privacy.section7.portability.title': 'Right to Data Portability',
    'privacy.section7.portability.text': 'You have the right to receive your personal data in a structured, commonly used and machine-readable format and the right to transfer the data to another controller, if processing is based on consent or contract and processing is carried out automatically.',
    'privacy.section7.withdraw.title': 'Right to Withdraw Consent',
    'privacy.section7.withdraw.text': 'If the processing of your personal data is based on your consent, you have the right to withdraw your consent at any time. Withdrawal of consent does not affect the lawfulness of processing based on consent before its withdrawal.',
    'privacy.section7.complaint.title': 'Right to Lodge a Complaint with a Supervisory Authority',
    'privacy.section7.complaint.text': 'You have the right to lodge a complaint with the competent supervisory authority (in Finland, the Office of the Data Protection Ombudsman, www.tietosuoja.fi) if you consider that the processing of your personal data violates the data protection regulation.',
    'privacy.section7.exercise.title': 'Exercising Your Rights:',
    'privacy.section7.exercise.intro': 'You can exercise your rights by contacting:',
    'privacy.section7.exercise.email': 'Email',
    'privacy.section7.exercise.phone': 'Phone',
    'privacy.section7.exercise.address': 'Postal address: FennoFuture, Turuntie 3, 37830 Akaa, Finland',
    'privacy.section7.exercise.response': 'We generally respond to your requests within one month. In complex cases, we may extend the deadline by two months, of which we will notify you separately.',
    'privacy.section8.title': '8. Data Security',
    'privacy.section8.text': 'We protect personal data appropriately with technical and organizational security measures. Data is processed confidentially and only authorized persons have access to it.',
    'privacy.section9.title': '9. Cookies',
    'privacy.section9.text': 'Our website may use cookies to improve the user experience. You can manage the use of cookies in your browser settings.',
    'privacy.section10.title': '10. Changes to the Privacy Policy',
    'privacy.section10.text': 'We reserve the right to update this privacy policy. Changes will be notified on our website.',
    'privacy.updated': 'Updated',
  },

  sv: {
    // Navigation
    'nav.home': 'Hem',
    'nav.services': 'Tjänster',
    'nav.pricing': 'Priser',
    'nav.faq': 'FAQ',
    'nav.shipping': 'Leverans',
    'nav.contact': 'Kontakt',
    'nav.callNow': 'Ring nu',

    // Homepage Hero
    'home.hero.badge': 'Propellerreparation och reservdelar',
    'home.hero.title': 'Pålitlig Propellerreparation',
    'home.hero.titleHighlight': 'i Birkaland',
    'home.hero.subtitle': 'Vi reparerar propellrar professionellt. Kontakta oss och berätta om dina behov!',
    'home.hero.cta.message': 'Skicka meddelande',
    'home.hero.cta.call': 'Ring +358 400 416 466',

    // Homepage Features
    'home.features.quality.title': 'Kvalitet före hastighet',
    'home.features.quality.desc': 'Noggrant arbete',
    'home.features.warranty.title': '12 månaders garanti',
    'home.features.warranty.desc': 'På alla reparationer',
    'home.features.satisfaction.title': 'Nöjdhetsgaranti',
    'home.features.satisfaction.desc': 'Siktar på funktionalitet',
    'home.features.experience.title': '100+ reparationer',
    'home.features.experience.desc': 'Mångsidig erfarenhet',

    // Homepage Services Preview
    'home.services.title': 'Våra tjänster',
    'home.services.subtitle': 'Omfattande propellerreparation och underhållstjänster',
    'home.services.repair.title': 'Propellerreparationer',
    'home.services.repair.desc': 'Rätning av böjda blad, spricksvetsning, skadereparation',
    'home.services.repair.price': 'Från €95',
    'home.services.maintenance.title': 'Underhåll & Renovering',
    'home.services.maintenance.desc': 'Balansering, polering, beläggning, antifouling-behandling',
    'home.services.maintenance.price': 'Från €80',
    'home.services.parts.title': 'Reservdelar',
    'home.services.parts.desc': 'Propellrar, motordelar, underhållsdelar och mer',
    'home.services.parts.price': 'Begär offert',
    'home.services.viewAll': 'Visa alla tjänster',
    'home.services.inspection.title': 'Inspektion',
    'home.services.inspection.desc': 'Grundlig tillståndsbedömning och dokumentation',

    // Homepage Process
    'home.process.title': 'Så fungerar reparationsprocessen',
    'home.process.subtitle': '7 tydliga steg',
    'home.process.step1.title': '1. Kontakt',
    'home.process.step1.desc': 'Kontakta oss via telefon, e-post eller använd kalkylatorn för prisuppskattning.',
    'home.process.step2.title': '2. Propellerleverans',
    'home.process.step2.desc': 'Ta med propellern till oss, skicka per post eller beställ hämtservice.',
    'home.process.step3.title': '3. Inspektion',
    'home.process.step3.desc': 'Vi inspekterar propellern noggrant och dokumenterar skadorna.',
    'home.process.step4.title': '4. Offert',
    'home.process.step4.desc': 'Vi kontaktar dig och meddelar priset.',
    'home.process.step5.title': '5. Reparation',
    'home.process.step5.desc': 'Efter godkänd offert påbörjar vi reparationen professionellt.',
    'home.process.step6.title': '6. Kvalitetskontroll',
    'home.process.step6.desc': 'Vi kontrollerar arbetskvaliteten, balanserar och testar.',
    'home.process.step7.title': '7. Leverans',
    'home.process.step7.desc': 'Vi meddelar när det är klart. Hämtning eller vi levererar.',

    // Homepage CTA
    'home.cta.title': 'Behöver du propellerreparation?',
    'home.cta.subtitle': 'Beräkna pris med gratis kalkylator eller kontakta oss direkt.',
    'home.cta.button': 'Kontakta oss',

    // Homepage Why Choose Us
    'home.why.title': 'Varför välja oss?',
    'home.why.warranty.title': 'Garanti',
    'home.why.warranty.desc': '12 månaders garanti på allt arbete',
    'home.why.expertise.title': 'Expertis',
    'home.why.expertise.desc': 'Erfarenhet av mångsidiga reparationer',
    'home.why.quality.title': 'Kvalitet',
    'home.why.quality.desc': 'Vi använder kvalitetsmaterial och metoder',
    'home.why.flexibility.title': 'Flexibilitet',
    'home.why.flexibility.desc': 'Även brådskande jobb och expressservice',
    'home.why.service.title': 'Kundservice',
    'home.why.service.desc': 'Personlig service på finska och engelska',
    'home.why.pricing.title': 'Transparent prissättning',
    'home.why.pricing.desc': 'Tydliga priser utan överraskningar',

    // Services Page
    'services.title': 'Våra tjänster',
    'services.subtitle': 'Omfattande propellerreparation och underhållstjänster. Expertis och kvalitet.',
    'services.repair.title': 'Propellerreparationer',
    'services.repair.desc': 'Professionell reparationsservice för alla propellertyper',
    'services.repair.feature1': 'Rätning av böjda blad',
    'services.repair.feature2': 'Spricksvetsning',
    'services.repair.feature3': 'Reparation av skador',
    'services.repair.feature4': 'Sprickreparationer',
    'services.repair.feature5': 'Kavitationsskadereparation',
    'services.maintenance.title': 'Underhåll & Renovering',
    'services.maintenance.desc': 'Regelbundet underhåll förlänger propellerns livslängd',
    'services.maintenance.feature1': 'Balansering',
    'services.maintenance.feature2': 'Polering',
    'services.maintenance.feature3': 'Beläggning',
    'services.maintenance.feature4': 'Antifouling-behandling',
    'services.maintenance.feature5': 'Bussningsbyte',
    'services.parts.title': 'Reservdelar',
    'services.parts.desc': 'Propellrar, motordelar, underhållsdelar och mer',
    'services.parts.feature1': 'Propellrar för alla motorer',
    'services.parts.feature2': 'Motordelar',
    'services.parts.feature3': 'Underhållsdelar',
    'services.parts.feature4': 'Installation tillgänglig',
    'services.parts.feature5': 'Rådgivning och rekommendationer',
    'services.inspection.title': 'Inspektion och skaderapporter',
    'services.inspection.desc': 'Grundlig tillståndsbedömning och dokumentation',
    'services.inspection.feature1': 'Visuell inspektion',
    'services.inspection.feature2': 'Mätningar',
    'services.inspection.feature3': 'Skadekartläggning',
    'services.inspection.feature4': 'Skriftlig rapport',
    'services.inspection.feature5': 'Reparationsrekommendation',
    'services.emergency.title': 'Expressservice',
    'services.emergency.desc': 'Brådskande reparationer med snabb leverans',
    'services.emergency.feature1': 'Snabba reparationer',
    'services.emergency.feature2': 'Tillfälliga lösningar',
    'services.emergency.feature3': 'Hämtning och leverans',
    'services.emergency.feature4': 'Reservpropellrar',
    'services.emergency.feature5': 'Prioriterad schemaläggning',
    'services.whyChoose': 'Varför välja oss?',
    'services.from': 'Från',
    'services.askQuote': 'Begär offert',
    'services.repair.price': 'Från €95',
    'services.maintenance.price': 'Från €50',
    'services.inspection.price': 'Från €30',
    'services.emergency.price': '0-24h: +100%',

    // Pricing Page
    'pricing.title': 'Priser',
    'pricing.subtitle': 'Välj alternativ nedan och beräkna uppskattat pris',
    'pricing.material': 'Material',
    'pricing.material.note': 'I vissa fall kan en ny propeller vara ett billigare alternativ.',
    'pricing.material.titanium': 'Titan',
    'pricing.material.titanium.desc': 'Premiummaterial för speciell användning',
    'pricing.material.bronze': 'Brons',
    'pricing.material.bronze.desc': 'Traditionell för marin användning',
    'pricing.material.stainless': 'Rostfritt stål',
    'pricing.material.stainless.desc': 'Hållbart och starkt material',
    'pricing.material.aluminum': 'Aluminium',
    'pricing.material.aluminum.desc': 'Lätt och vanligt material',
    'pricing.size': 'Propellerstorlek',
    'pricing.size.basic': 'Grundstorlek',
    'pricing.size.medium': 'Mellanstorlek',
    'pricing.size.large': 'Stora propellrar',
    'pricing.size.xlarge': 'Extra stora',
    'pricing.size.professional': 'Professionell användning',
    'pricing.duoprop': 'Duoprop (dubbelpropeller)',
    'pricing.services': 'Tilläggstjänster',
    'pricing.services.duopropNote': 'Duoprop vald: markerade tjänster x2',
    'pricing.services.balancing': 'Balansering',
    'pricing.services.balancing.desc': 'Minskar vibrationer',
    'pricing.services.polishing': 'Polering',
    'pricing.services.polishing.desc': 'Förbättrar effektiviteten',
    'pricing.services.coating': 'Beläggning',
    'pricing.services.coating.desc': 'Skyddar mot korrosion',
    'pricing.services.bushing': 'Bussningsbyte',
    'pricing.services.bushing.desc': 'Utbytesarbete',
    'pricing.services.documentation': 'Dokumentation',
    'pricing.services.documentation.desc': 'Reparationsrapport',
    'pricing.services.inspection': 'Inspektionsavgift',
    'pricing.services.inspection.desc': 'Om reparation inte utförs',
    'pricing.services.shipping': 'Leverans',
    'pricing.services.shipping.desc': 'Returfrakt till kund',
    'pricing.urgency': 'Tidslinje',
    'pricing.urgency.normal': 'Normal',
    'pricing.urgency.normal.time': 'Enligt arbetskö',
    'pricing.urgency.urgent': 'Brådskande',
    'pricing.urgency.urgent.time': '2-4 dagar',
    'pricing.urgency.veryUrgent': 'Mycket brådskande',
    'pricing.urgency.veryUrgent.time': '1-2 dagar',
    'pricing.urgency.emergency': 'Akut',
    'pricing.urgency.emergency.time': 'Samma dag',
    'pricing.estimate': 'Prisuppskattning',
    'pricing.estimate.total': 'Uppskattat totalpris',
    'pricing.estimate.repair': 'Reparationsarbete',
    'pricing.estimate.services': 'Tilläggstjänster',
    'pricing.estimate.vat': 'Moms 25,5% ingår. Slutpris bekräftas efter inspektion.',
    'pricing.estimate.inspectionVat': 'Debiteras om reparation inte utförs. Moms 25,5% ingår.',
    'pricing.estimate.select': 'Välj material och storlek för att se prisuppskattning',
    'pricing.sendQuote': 'Skicka offertförfrågan',
    'pricing.help.title': 'Behöver du hjälp med prissättning?',
    'pricing.help.subtitle': 'Kontakta oss så hjälper vi dig',
    'pricing.help.call': 'Ring',
    'pricing.common': 'Vanlig',
    'pricing.starting': 'Från',
    'pricing.blades': 'Antal blad',
    'pricing.blades.label': 'blad',
    'pricing.blades.2': '2',
    'pricing.blades.3': '3',
    'pricing.blades.4': '4',
    'pricing.blades.5': '5',
    'pricing.blades.6plus': '6+',

    // Contact Page
    'contact.title': 'Kontakta oss',
    'contact.subtitle': 'Vi hjälper gärna till med alla frågor om propellerreparationer.',
    'contact.info': 'Kontaktinformation',
    'contact.phone': 'Telefon',
    'contact.phone.hours': 'Mån-Fre 8-16',
    'contact.email': 'E-post',
    'contact.email.response': 'Vi svarar inom 24h',
    'contact.location': 'Plats',
    'contact.location.area': 'Serviceområde: Akaa / Sastamala / Urjala',
    'contact.hours': 'Öppettider',
    'contact.hours.weekday': 'Måndag - Fredag: 8:00 - 16:00',
    'contact.hours.weekend': 'Helg: Enligt överenskommelse',
    'contact.questions': 'Frågor?',
    'contact.questions.desc': 'Ring oss så tar vi reda på hur vi kan hjälpa.',
    'contact.form.title': 'Skicka meddelande',
    'contact.form.estimate': 'Prisuppskattning från prissidan',
    'contact.form.prefilled': 'Information är förifylld. Fyll i dina kontaktuppgifter och skicka offertförfrågan.',
    'contact.form.name': 'Namn',
    'contact.form.email': 'E-post',
    'contact.form.phone': 'Telefon',
    'contact.form.propellerType': 'Propellertyp',
    'contact.form.propellerType.select': 'Välj...',
    'contact.form.propellerType.aluminum': 'Aluminium',
    'contact.form.propellerType.stainless': 'Rostfritt stål',
    'contact.form.propellerType.bronze': 'Brons',
    'contact.form.propellerType.other': 'Annan propellertyp',
    'contact.form.duoprop': 'Duoprop',
    'contact.form.priority': 'Brådskande / Prioritet',
    'contact.form.priority.normal': 'Normal',
    'contact.form.priority.normal.desc': 'Ingen brådska',
    'contact.form.priority.moderate': 'Måttlig',
    'contact.form.priority.moderate.desc': '1-2 veckor',
    'contact.form.priority.urgent': 'Brådskande',
    'contact.form.priority.urgent.desc': 'Under en vecka',
    'contact.form.priority.emergency': 'Akut',
    'contact.form.priority.emergency.desc': 'Omedelbart',
    'contact.form.delivery': 'Leverans',
    'contact.form.delivery.bring': 'Jag tar med den själv',
    'contact.form.delivery.send': 'Jag skickar per post',
    'contact.form.delivery.pickup': 'Jag önskar hämtservice',
    'contact.form.message': 'Meddelande',
    'contact.form.message.placeholder': 'Berätta om propellerskadan och vilken reparation du behöver...',
    'contact.form.images': 'Bilder (valfritt)',
    'contact.form.images.desc': 'Lägg till bilder av skadan - max 5 bilder, 5 MB / bild',
    'contact.form.images.add': 'Lägg till bilder',
    'contact.form.images.addMore': 'Lägg till fler bilder',
    'contact.form.submit': 'Skicka meddelande',
    'contact.form.submitting': 'Skickar...',
    'contact.form.success': 'Meddelande skickat! Vi svarar så snart som möjligt.',
    'contact.form.error': 'Kunde inte skicka meddelande. Försök igen eller kontakta per telefon.',
    'contact.form.validation.contact': 'Vänligen ange antingen e-post eller telefonnummer (eller båda)',
    'contact.form.validation.tooFast': 'Formuläret fylldes i för snabbt. Vänta en stund och försök igen.',
    'contact.form.validation.rateLimit': 'Vänta {seconds} sekunder före nästa inskickning.',
    'contact.form.validation.maxImages': 'Du kan lägga till maximalt {max} bilder',
    'contact.form.validation.imageType': 'Endast bildfiler tillåtna',
    'contact.form.validation.imageSize': 'Maximal bildstorlek är 5 MB',
    'contact.map': 'Plats',

    // FAQ Page
    'faq.title': 'Vanliga frågor',
    'faq.subtitle': 'Svar på vanliga frågor om propellerreparationer',
    'faq.q1': 'Hur mycket kostar propellerreparation?',
    'faq.a1': 'Priserna börjar från €95 för enkla reparationer. Eftersom det är hantverksarbete kanske tjänsten inte är en rimlig lösning för alla. Slutpriset beror på skadans omfattning, propellerstorlek och nödvändigt arbete. Använd vår gratis kalkylator för en omedelbar uppskattning eller kontakta oss för en exakt offert.',
    'faq.q2': 'Hur lång tid tar reparationen?',
    'faq.a2': 'Normala reparationer är klara på 3-7 dagar. Observera att arbetskön kan påverka leveranstiden. Komplexa reparationer kan kräva mer tid. Vi meddelar den exakta leveranstiden i offerten.',
    'faq.q3': 'Vilka typer av propellrar reparerar ni?',
    'faq.a3': 'Vi reparerar huvudsakligen båtpropellrar av aluminium, rostfritt stål och bronslegeringar. Vi har också servat en del waterjet-impellrar.',
    'faq.q4': 'Erbjuder ni hämtning och leveransservice?',
    'faq.a4': 'Ja. Vi erbjuder hämtning och leveransservice mot extra avgift. Du kan också skicka propellern till oss per post eller ta med den till verkstaden själv. Se mer detaljer på vår Leveranssida.',
    'faq.q5': 'Finns det garanti på reparationer?',
    'faq.a5': 'Ja! Alla reparationer har 12 månaders garanti. Garantin täcker arbetsfel och materialdefekter. Garantin täcker inte normalt slitage eller nya skador.',
    'faq.q6': 'Kan ni reparera en svårt skadad propeller?',
    'faq.a6': 'I de flesta fall, ja! Vi reparerar även svårt skadade propellrar: böjda blad, sprickor, skador, kavitationsskador. Vi bedömer alltid först om reparation är ekonomiskt rimligt vs. en ny propeller.',
    'faq.q7': 'Gör ni brådskande reparationer?',
    'faq.a7': 'Ja! Vi erbjuder expressservice mot extra avgift. Ring direkt +358 400 416 466 om du behöver brådskande reparation. Se mer detaljer på vår Prissida.',
    'faq.q8': 'Hur kan jag betala?',
    'faq.a8': 'Vi accepterar kontant, banköverföring och faktura (företagskunder). Betalning sker innan propellern lämnas ut. Vi skickar fakturan via e-post så fort arbetet är klart. Kontant betalning är möjlig vid hämtning, kvitto skickas via e-post.',
    'faq.noAnswer': 'Hittade du inget svar?',
    'faq.noAnswerDesc': 'Kontakta oss så svarar vi på din fråga personligen.',

    // Shipping Page
    'shipping.title': 'Leverans & Logistik',
    'shipping.subtitle': 'Flera sätt att leverera propellern till oss och tillbaka.',
    'shipping.options': 'Leveransalternativ',
    'shipping.bringYourself': 'Ta med själv',
    'shipping.bringYourselfDesc': 'Snabbaste och enklaste sättet. Vi arbetar i en verkstad där dörrarna är låsta och vi inte alltid är närvarande. Besök bokas i förväg.',
    'shipping.free': 'Gratis',
    'shipping.byAppointment': 'Enligt överenskommelse mån-fre 8-16',
    'shipping.postIt': 'Posta till oss',
    'shipping.postItDesc': 'Packa omsorgsfullt och skicka via Matkahuolto/Post. Instruktioner nedan.',
    'shipping.ownCost': 'Egen kostnad',
    'shipping.usually': 'Vanligtvis €10-25',
    'shipping.mostPopular': 'Mest populär',
    'shipping.pickupService': 'Hämtservice',
    'shipping.pickupServiceDesc': 'Vi hämtar propellern hos dig och levererar tillbaka när den är klar.',
    'shipping.perKm': '€1,21/km',
    'shipping.dependsOnDistance': 'Priset beror på avståndet',
    'shipping.calculatePrice': 'Beräkna pris',
    'shipping.priceCalculator': 'Hämtservice priskalkylator',
    'shipping.fromAddress': 'Startadress',
    'shipping.addressPlaceholder': 'Ange din adress',
    'shipping.serviceType': 'Tjänsttyp',
    'shipping.pickupOnly': 'Endast hämtning',
    'shipping.deliveryOnly': 'Endast leverans',
    'shipping.pickupAndDelivery': 'Hämtning och leverans',
    'shipping.oneWayDistance': 'Avstånd enkel väg',
    'shipping.pricePerKm': 'Pris per km',
    'shipping.estimatedPrice': 'Uppskattat pris',
    'shipping.priceNote': 'Obs: Detta är en uppskattning. Slutpris bekräftas i offert.',
    'shipping.packagingInstructions': 'Förpackningsinstruktioner för post',
    'shipping.packStep1': 'Torka propellern före packning',
    'shipping.packStep2': 'Använd en stadig kartong eller trälåda',
    'shipping.packStep3': 'Fyll tomma utrymmen med tidningar, bubbelplast eller liknande',
    'shipping.packStep4': 'Bifoga dina kontaktuppgifter och en kort beskrivning av skadan',
    'shipping.pickupArea': 'Hämtserviceområde',
    'shipping.pickupAreaDesc': 'Vi erbjuder hämtservice huvudsakligen i Birkalandsregionen.',
    'shipping.deliveryTime': 'Leveranstid tillbaka',
    'shipping.pickupTime': 'Hämtning: Avtalas separat',
    'shipping.postTime': 'Post: 1-3 arbetsdagar',
    'shipping.needHelp': 'Behöver du hjälp med leverans?',
    'shipping.needHelpDesc': 'Ring oss så hjälper vi dig på bästa sätt.',

    // Footer
    'footer.services': 'Tjänster',
    'footer.contact': 'Kontakt',
    'footer.hours': 'Öppettider',
    'footer.weekdays': 'Mån-Fre: 8-16',
    'footer.weekend': 'Lör-Sön: Enligt överenskommelse',
    'footer.privacy': 'Integritetspolicy',
    'footer.terms': 'Användarvillkor',
    'footer.copyright': 'Alla rättigheter förbehållna.',
    'footer.tagline': 'Professionell propellerreparation',
    'footer.description': 'Vi reparerar båtpropellrar professionellt. Aluminium-, stål- och bronspropellrar. 12 månaders garanti på allt arbete.',
    'footer.businessId': 'Organisationsnummer',
    'footer.founded': 'Grundat',
    'footer.quickLinks': 'Snabblänkar',
    'footer.madeWith': 'Gjord med',
    'footer.inFinland': 'i Finland',

    // Common
    'common.learnMore': 'Läs mer',
    'common.callNow': 'Ring nu',
    'common.from': 'Från',
    'common.yes': 'Ja',
    'common.no': 'Nej',
    'common.notProvided': 'Ej angiven',

    // Terms Page
    'terms.title': 'Användarvillkor',
    'terms.subtitle': 'Användarvillkor för Propellerreparation webbplats och tjänster',
    'terms.section1.title': '1. Allmänna villkor och tillämpningsområde',
    'terms.section1.p1': 'Dessa allmänna villkor ("Villkor") gäller för produkter och tjänster som erbjuds av FennoFuture (organisationsnummer 3203489-7, "Företaget", "vi", "vår") samt användning av webbplatsen. Genom att använda våra tjänster eller göra en beställning accepterar du dessa villkor och förbinder dig att följa dem.',
    'terms.section1.p2': 'Dessa villkor utgör ett juridiskt bindande avtal mellan kunden och företaget. Vi förbehåller oss rätten att ändra dessa villkor genom att meddela ändringar på vår webbplats minst 30 dagar innan ändringarna träder i kraft. Befintliga avtal omfattas av de villkor som gällde vid beställningstillfället.',
    'terms.section2.title': '2. Användning av tjänsten och skyldigheter',
    'terms.section2.intro': 'Kunden förbinder sig att använda våra tjänster och webbplats:',
    'terms.section2.item1': 'I enlighet med gällande finsk lagstiftning och EU-förordningar',
    'terms.section2.item2': 'I enlighet med dessa användarvillkor och eventuella separata avtal',
    'terms.section2.item3': 'I enlighet med god affärssed och yrkesetik',
    'terms.section2.item4': 'Med respekt för andra användares, tredje parters och företagets rättigheter',
    'terms.section2.item5': 'Med korrekt och aktuell information',
    'terms.section2.p2': 'Kunden ansvarar för alla sina handlingar och försummelser vid användning av tjänsterna. Kunden förbinder sig att ersätta företaget för alla skador som uppstår till följd av handlingar som strider mot dessa villkor eller tillämplig lagstiftning.',
    'terms.section3.title': '3. Beställningar, offerter och avtal',
    'terms.section3.intro': 'Leverans av beställningar och tjänster baseras på följande principer:',
    'terms.section3.item1': 'Alla våra offerter gäller i 30 dagar från utfärdandedatumet, om inte annat anges',
    'terms.section3.item2': 'Godkännande av en offert bildar ett bindande avtal mellan parterna',
    'terms.section3.item3': 'Vi förbehåller oss rätten att vägra ta emot en beställning utan motivering',
    'terms.section3.item4': 'Leveranstider är uppskattningar och kan variera beroende på omständigheter',
    'terms.section3.item5': 'Avtalet omfattas av de priser och villkor som gäller vid beställningstillfället',
    'terms.section3.item6': 'Skriftliga avtal och bekräftelser ersätter muntliga avtal',
    'terms.section3.p2': 'Vi förbehåller oss rätten att göra rimliga ändringar i leveranser om de beror på tekniska skäl, tillgång på material eller andra liknande motiverade skäl. Väsentliga ändringar meddelas kunden utan dröjsmål.',
    'terms.section4.title': '4. Prissättning och moms',
    'terms.section4.intro': 'Följande principer gäller för prissättning:',
    'terms.section4.item1': 'Priser tillkommer gällande mervärdesskatt (moms), om inte annat anges',
    'terms.section4.item2': 'Priser gäller vid tidpunkten för offert eller orderbekräftelse',
    'terms.section4.item3': 'Vi förbehåller oss rätten att ändra priser utan förvarning för nya beställningar',
    'terms.section4.item4': 'Befintliga beställningar omfattas av de priser som överenskommits vid beställningstillfället',
    'terms.section4.item5': 'Eventuella leverans-, installations- och andra tilläggskostnader anges separat',
    'terms.section4.p2': 'Om momssatser ändras mellan beställning och leverans, tillämpas skattesatserna vid leveranstillfället. På grund av prisändringar kan kunden avbeställa utan kostnad om prisändringen överstiger 10%.',
    'terms.section5.title': '5. Betalning och fakturering',
    'terms.section5.intro': 'Betalningsvillkor och -praxis:',
    'terms.section5.consumer': 'Konsumentkunder (propellerreparation):',
    'terms.section5.consumerText': 'Konsumentkunder betalar för arbete i förväg innan arbetet påbörjas. Betalning kan göras via banköverföring eller kort. Arbetet påbörjas inte förrän betalning har mottagits.',
    'terms.section5.business': 'Företagskunder:',
    'terms.section5.businessText': 'För företagskunder är betalningsvillkoret 7 dagar netto från fakturadatum, om inte annat överenskommits. Förskottsbetalning kan krävas för första beställningen.',
    'terms.section5.item1': 'Försenad betalning medför dröjsmålsränta enligt räntelagen',
    'terms.section5.item2': 'Vi behåller äganderätten till produkter tills betalning har gjorts i sin helhet',
    'terms.section5.item3': 'Obetalda fakturor överlämnas till inkasso i enlighet med konsumentskyddslagen',
    'terms.section5.item4': 'Inkassokostnader och rättegångskostnader är kundens ansvar',
    'terms.section5.item5': 'Vi accepterar banköverföring, kortbetalning och faktura (företagskunder) som betalningsmetoder',
    'terms.section5.p2': 'Om kunden inte betalar har vi rätt att avbryta arbetet och/eller kräva hela det återstående beloppet omedelbart. Betalningsförsening ger oss rätt att säga upp avtalet och kräva ersättning.',
    'terms.section6.title': '6. Garanti och felansvar',
    'terms.section6.intro': 'Garantivillkor och ansvarsbegränsningar:',
    'terms.section6.item1': 'Vi erbjuder 12 månaders begränsad garanti för tillverknings- och installationsfel',
    'terms.section6.item2': 'Garantin kräver korrekt användning av produkten/tjänsten under normala förhållanden',
    'terms.section6.item3': 'Garantin täcker inte normalt slitage, missbruk eller oavsiktlig skada',
    'terms.section6.item4': 'Garantireparationer utförs inom rimlig tid enligt vår arbetsordning',
    'terms.section6.item5': 'Vi ansvarar för felansvar i enlighet med konsumentskyddslagen',
    'terms.section6.item6': 'Indirekta skador (t.ex. produktionsavbrott, inkomstförlust) ingår inte i vårt ansvar',
    'terms.section6.item7': 'Vårt maximala ansvar är begränsat till beställningens värde',
    'terms.section6.p2': 'För företagskunder ansvarar vi endast för direkta skador som beror på grov vårdslöshet eller uppsåtliga handlingar. Tvingande konsumentskyddsbestämmelser gäller för konsumentkunder.',
    'terms.section7.title': '7. Immateriella rättigheter och sekretess',
    'terms.section7.p1': 'Allt webbplatsinnehåll, inklusive men inte begränsat till texter, bilder, grafik, logotyper, databasinnehåll, designer, ritningar och programvarukod, tillhör företaget eller dess licensgivare och skyddas av upphovsrätt, varumärke och andra immaterialrättslagar.',
    'terms.section7.intro': 'Innehåll får inte:',
    'terms.section7.item1': 'Kopieras, reproduceras eller distribueras utan skriftligt tillstånd',
    'terms.section7.item2': 'Modifieras, omvandlas eller användas för att skapa derivatverk',
    'terms.section7.item3': 'Användas för kommersiella ändamål utan tillstånd',
    'terms.section7.item4': 'Få upphovsrätts- eller varumärkesmeddelanden borttagna',
    'terms.section7.p2': 'Kunden ger företaget en icke-exklusiv, royaltyfri licens att använda och utnyttja information som tillhandahålls av kunden för att leverera tjänsterna. Båda parter förbinder sig att hålla all affärsrelaterad information konfidentiell.',
    'terms.section8.title': '8. Ånger- och returrätt',
    'terms.section8.intro': 'Konsumenter har 14 dagars ångerrätt enligt konsumentskyddslagen, om det inte gäller:',
    'terms.section8.item1': 'Anpassade produkter eller produkter tillverkade enligt kundspecifikationer',
    'terms.section8.item2': 'Tjänster som har utförts helt med uttryckligt samtycke från konsumenten',
    'terms.section8.item3': 'Brådskande reparations- eller underhållsarbete som uttryckligen begärts av konsumenten',
    'terms.section8.item4': 'Lättfördärvliga eller snabbt utgående produkter',
    'terms.section8.p2': 'Företagskunder har ingen lagstadgad ångerrätt. Vid avbeställning återbetalas redan gjorda betalningar, minus rimliga kostnader för redan utfört arbete eller inköpta material.',
    'terms.section8.urgentCancel': 'Avbeställning av brådskande reparationer: Om kunden avbeställer en överenskommen brådskande reparation mindre än 48 timmar före överenskommen starttid, debiterar vi 25% av totalkostnaden, dock minst 50 € + moms. Avbeställningsavgiften täcker förlorade arbetstimmar och planeringsarbete.',
    'terms.section9.title': '9. Reklamationer och preskription av felansvar',
    'terms.section9.intro': 'Följande villkor gäller för reklamationsfrister och -förfaranden:',
    'terms.section9.consumer': 'Konsumenter:',
    'terms.section9.consumerText': 'Fel måste reklameras inom rimlig tid efter upptäckten av felet. Vi rekommenderar att reklamera senast 2 veckor efter upptäckten av felet. Konsumentskyddslagens preskriptionstider förblir i kraft.',
    'terms.section9.business': 'Företagskunder:',
    'terms.section9.businessText': 'Reklamation måste göras senast 1 månad efter upptäckten av felet eller när felet borde ha upptäckts. Reklamationen måste göras skriftligen och måste specificera felet tillräckligt detaljerat.',
    'terms.section9.p2': 'Reklamationer ska riktas till: innovation@fennofuture.fi eller +358 400 416 466. Vi svarar på reklamationer utan dröjsmål och strävar efter att lösa ärendet inom 14 dagar.',
    'terms.section10.title': '10. Force Majeure',
    'terms.section10.intro': 'Vi ansvarar inte för förseningar, skador eller annat avtalsbrott orsakat av force majeure, såsom:',
    'terms.section10.item1': 'Naturkatastrofer (översvämning, storm, jordbävning)',
    'terms.section10.item2': 'Krig, upplopp, terrorattacker',
    'terms.section10.item3': 'Strejker, lockouter eller andra arbetsmarknadsåtgärder',
    'terms.section10.item4': 'Myndighetsåtgärder eller lagändringar',
    'terms.section10.item5': 'Energi- eller råvarubrist',
    'terms.section10.item6': 'Brand, explosion eller annan liknande skadehändelse',
    'terms.section10.item7': 'Försening av underleverantör eller leverantör på grund av force majeure',
    'terms.section10.item8': 'Pandemier, epidemier eller andra exceptionella hälsorisker',
    'terms.section10.p2': 'Vid force majeure meddelar vi kunden utan dröjsmål och försöker minimera skadan. Om force majeure varar mer än 3 månader har endera parten rätt att säga upp avtalet utan ersättning.',
    'terms.section11.title': '11. Dataskydd och behandling av personuppgifter',
    'terms.section11.text': 'Vi behandlar personuppgifter i enlighet med EU:s allmänna dataskyddsförordning (GDPR) och finsk dataskyddslagstiftning. Detaljerad information om behandling av personuppgifter finns i vår separata',
    'terms.section11.link': 'integritetspolicy',
    'terms.section12.title': '12. Ändringar av villkor',
    'terms.section12.text': 'Vi förbehåller oss rätten att ändra dessa villkor. Ändringar meddelas på vår webbplats minst 30 dagar innan de träder i kraft. Vi meddelar även registrerade kunder om väsentliga ändringar via e-post. Befintliga avtal omfattas av de villkor som gällde vid avtalstillfället, om inte lagstiftningen kräver annat. Genom att fortsätta använda våra tjänster efter att ändringar trätt i kraft godkänner du de ändrade villkoren.',
    'terms.section13.title': '13. Tillämplig lag och tvistlösning',
    'terms.section13.p1': 'Dessa villkor och deras tolkning regleras av finsk lag, exklusive lagvalsregler. Hänvisning till finsk lag inkluderar även tillämpliga EU-förordningar.',
    'terms.section13.p2': 'Tvister löses i första hand genom förhandlingar mellan parterna. Om ingen uppgörelse nås inom 30 dagar, avgörs tvisten i första instans av Birkalands tingsrätt i Tammerfors.',
    'terms.section13.p3': 'Konsumenter har rätt att föra tvisten till Konsumenttvistenämnden (www.kuluttajariita.fi) eller EU:s plattform för tvistlösning online (ec.europa.eu/consumers/odr). Konsumenten kan även väcka talan vid tingsrätten på sin egen hemort.',
    'terms.section14.title': '14. Kontaktuppgifter och kundservice',
    'terms.section14.businessId': 'Organisationsnummer',
    'terms.section14.address': 'Adress',
    'terms.section14.email': 'E-post',
    'terms.section14.phone': 'Telefon',
    'terms.section14.customerService': 'Kundservice: mån-fre 8:00-16:00',
    'terms.updated': 'Uppdaterad',
    'terms.version': 'Version',

    // Privacy Page
    'privacy.title': 'Integritetspolicy',
    'privacy.subtitle': 'Propellerreparation - Behandling av personuppgifter',
    'privacy.section1.title': '1. Personuppgiftsansvarig',
    'privacy.section1.businessId': 'Organisationsnummer',
    'privacy.section1.email': 'E-post',
    'privacy.section1.phone': 'Telefon',
    'privacy.section2.title': '2. Registrets namn',
    'privacy.section2.text': 'FennoFuture kundregister och webbplatsens användarregister',
    'privacy.section3.title': '3. Ändamål med behandling av personuppgifter',
    'privacy.section3.intro': 'Vi behandlar personuppgifter för följande ändamål:',
    'privacy.section3.item1': 'Hantering och underhåll av kundrelationer',
    'privacy.section3.item2': 'Behandling av offerter och beställningar',
    'privacy.section3.item3': 'Fakturering och betalningstransaktioner',
    'privacy.section3.item4': 'Svar på förfrågningar',
    'privacy.section3.item5': 'Utveckling av tjänster',
    'privacy.section3.item6': 'Marknadsföring (baserat på samtycke)',
    'privacy.section4.title': '4. Registrets innehåll',
    'privacy.section4.intro': 'Registret kan innehålla följande information:',
    'privacy.section4.item1': 'Namn',
    'privacy.section4.item2': 'Kontaktuppgifter (adress, telefonnummer, e-post)',
    'privacy.section4.item3': 'Företagsnamn och organisationsnummer',
    'privacy.section4.item4': 'Faktureringsinformation',
    'privacy.section4.item5': 'Information relaterad till kundrelationen',
    'privacy.section4.item6': 'Webbplatsanvändningsdata (cookies, IP-adress)',
    'privacy.section5.title': '5. Lagringstid för uppgifter',
    'privacy.section5.text': 'Vi behåller personuppgifter så länge det är nödvändigt för att uppfylla behandlingens syfte eller under den tid som krävs enligt lag. Kunduppgifter behålls under minst den tid som krävs enligt bokföringslagen (6 år).',
    'privacy.section6.title': '6. Utlämnande av uppgifter',
    'privacy.section6.intro': 'Vi lämnar inte ut personuppgifter till tredje part utan ditt samtycke, förutom:',
    'privacy.section6.item1': 'För att uppfylla en juridisk skyldighet',
    'privacy.section6.item2': 'Till partners som är nödvändiga för att tillhandahålla tjänsten (t.ex. betaltjänster)',
    'privacy.section6.item3': 'På begäran av myndigheter i fall som krävs enligt lag',
    'privacy.section7.title': '7. Den registrerades rättigheter',
    'privacy.section7.intro': 'Du har följande rättigheter enligt GDPR avseende behandling av dina personuppgifter:',
    'privacy.section7.access.title': 'Rätt till tillgång',
    'privacy.section7.access.text': 'Du har rätt att få veta om dina personuppgifter behandlas, och i så fall rätt att få tillgång till personuppgifterna och få en kopia av de personuppgifter som behandlas. Du kan begära information om behandlingens syfte, datakategorier, mottagare och lagringstider.',
    'privacy.section7.rectification.title': 'Rätt till rättelse',
    'privacy.section7.rectification.text': 'Om dina personuppgifter är felaktiga eller ofullständiga har du rätt att begära rättelse eller komplettering. Vi strävar efter att korrigera fel utan dröjsmål, senast inom en månad från begäran.',
    'privacy.section7.erasure.title': 'Rätt till radering ("Rätten att bli glömd")',
    'privacy.section7.erasure.text1': 'Du har rätt att begära radering av dina personuppgifter i vissa situationer, till exempel när uppgifterna inte längre behövs för de ändamål de samlades in för, eller om du återkallar ditt samtycke.',
    'privacy.section7.erasure.text2': 'Viktig begränsning: Vi kan inte radera personuppgifter som vi enligt lag är skyldiga att behålla. Enligt bokföringslagen måste affärstransaktionsdata (såsom fakturor, avtal och betalningskvitton) behållas i minst sex (6) år från räkenskapsårets slut. Dessa uppgifter kan inte raderas innan den lagstadgade lagringstiden löper ut, även om radering begärs. I sådana fall begränsar vi behandlingen av uppgifter till endast lagstadgad lagring.',
    'privacy.section7.restriction.title': 'Rätt till begränsning av behandling',
    'privacy.section7.restriction.text': 'Du har rätt att begära begränsning av behandlingen av dina personuppgifter i vissa situationer, till exempel om du bestrider uppgifternas korrekthet eller invänder mot behandling. Under begränsningen lagras uppgifterna men behandlas inte i övrigt.',
    'privacy.section7.objection.title': 'Rätt att invända',
    'privacy.section7.objection.text': 'Du har rätt att invända mot behandlingen av dina personuppgifter av skäl som hänför sig till din speciella situation, när behandlingen grundar sig på allmänt intresse eller vårt berättigade intresse. Vi bedömer då om vi har tvingande berättigade skäl för behandling som åsidosätter den registrerades intressen och rättigheter.',
    'privacy.section7.portability.title': 'Rätt till dataportabilitet',
    'privacy.section7.portability.text': 'Du har rätt att få dina personuppgifter i ett strukturerat, allmänt använt och maskinläsbart format och rätt att överföra uppgifterna till en annan personuppgiftsansvarig, om behandlingen grundar sig på samtycke eller avtal och behandlingen utförs automatiskt.',
    'privacy.section7.withdraw.title': 'Rätt att återkalla samtycke',
    'privacy.section7.withdraw.text': 'Om behandlingen av dina personuppgifter grundar sig på ditt samtycke har du rätt att återkalla ditt samtycke när som helst. Återkallande av samtycke påverkar inte lagligheten av behandling baserad på samtycke före återkallandet.',
    'privacy.section7.complaint.title': 'Rätt att lämna in klagomål till tillsynsmyndighet',
    'privacy.section7.complaint.text': 'Du har rätt att lämna in ett klagomål till behörig tillsynsmyndighet (i Finland Dataombudsmannens byrå, www.tietosuoja.fi) om du anser att behandlingen av dina personuppgifter bryter mot dataskyddsförordningen.',
    'privacy.section7.exercise.title': 'Utöva dina rättigheter:',
    'privacy.section7.exercise.intro': 'Du kan utöva dina rättigheter genom att kontakta:',
    'privacy.section7.exercise.email': 'E-post',
    'privacy.section7.exercise.phone': 'Telefon',
    'privacy.section7.exercise.address': 'Postadress: FennoFuture, Turuntie 3, 37830 Akaa, Finland',
    'privacy.section7.exercise.response': 'Vi svarar i regel på dina förfrågningar inom en månad. I komplexa fall kan vi förlänga tidsfristen med två månader, vilket vi meddelar dig separat.',
    'privacy.section8.title': '8. Datasäkerhet',
    'privacy.section8.text': 'Vi skyddar personuppgifter på lämpligt sätt med tekniska och organisatoriska säkerhetsåtgärder. Uppgifter behandlas konfidentiellt och endast behöriga personer har tillgång till dem.',
    'privacy.section9.title': '9. Cookies',
    'privacy.section9.text': 'Vår webbplats kan använda cookies för att förbättra användarupplevelsen. Du kan hantera användningen av cookies i webbläsarens inställningar.',
    'privacy.section10.title': '10. Ändringar av integritetspolicyn',
    'privacy.section10.text': 'Vi förbehåller oss rätten att uppdatera denna integritetspolicy. Ändringar meddelas på vår webbplats.',
    'privacy.updated': 'Uppdaterad',
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first
    const saved = localStorage.getItem('language') as Language;
    if (saved && ['fi', 'en', 'sv'].includes(saved)) {
      return saved;
    }
    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'sv') return 'sv';
    if (browserLang === 'en') return 'en';
    return 'fi'; // Default to Finnish
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key] || translations['fi'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
